import { v4 as uuidv4 } from 'uuid';
import { 
    bffGetAuthCode, 
    bffGetAuthToken, 
    bffExecuteInsuranceAction, 
    bffRegisterLink 
} from './bffService';
import { accelerateWebhookPolling } from '../webhook/webhookService';


// Variáveis globais para gerenciar o token
let currentToken = null;
let tokenExpiresAt = null;

export const getBffBaseUrl = () => process.env.REACT_APP_BFF_API_URL || 'https://vpytjjfrusp6c54x2hrumxovdm0zmhpw.lambda-url.us-east-1.on.aws';
export const getApiBaseUrl = () => process.env.REACT_APP_API_BASE_URL || `${getBffBaseUrl()}/api`;
export const getOrganizationId = () => process.env.REACT_APP_ORGANIZATION_ID || 'fleury';

function getAuthHeader() {
    const token = currentToken || process.env.REACT_APP_API_CONVERSATION_TOKEN;
    return token ? { 'Authorization': `Bearer ${token}` } : {};
}
let featureFlagsCache = {
    clientId: null,
    loadedAt: null,
    flags: {},
    source: null,
    timestamp: null,
    error: null,
    promise: null
};

function normalizeFeatureFlagsPayload(payload) {
    if (!payload || typeof payload !== 'object') {
        return {
            flags: {},
            source: null,
            timestamp: null
        };
    }

    return {
        flags: payload.flags && typeof payload.flags === 'object' ? payload.flags : {},
        source: payload.source || null,
        timestamp: payload.timestamp || null
    };
}

function syncFeatureFlagsToWindow(cacheData) {
    if (typeof window === 'undefined') {
        return;
    }

    window.currentFeatureFlags = cacheData?.flags || {};
}

export function getCachedFeatureFlags() {
    return featureFlagsCache?.flags || {};
}

export function getFeatureFlagsSnapshot() {
    return {
        flags: getCachedFeatureFlags(),
        source: featureFlagsCache?.source || null,
        timestamp: featureFlagsCache?.timestamp || null,
        loadedAt: featureFlagsCache?.loadedAt || null,
        clientId: featureFlagsCache?.clientId || null
    };
}

export function buildFeatureFlagsContext() {
    const snapshot = getFeatureFlagsSnapshot();
    return {
        featureFlags: snapshot.flags,
        featureFlagsMetadata: {
            source: snapshot.source,
            timestamp: snapshot.timestamp,
            loadedAt: snapshot.loadedAt,
            clientId: snapshot.clientId
        }
    };
}

export async function initializeFeatureFlagsCache(client_id) {
    const featureFlagsApiBaseUrl = process.env.REACT_APP_FEATURE_FLAGS_API_URL || `${getBffBaseUrl()}/api`;
    const resolvedClientId = client_id || 'web-default';

    if (!featureFlagsApiBaseUrl) {
        console.warn('REACT_APP_FEATURE_FLAGS_API_URL não configurada. Seguindo sem feature flags.');
        featureFlagsCache = {
            ...featureFlagsCache,
            clientId: resolvedClientId,
            loadedAt: new Date().toISOString(),
            flags: {},
            source: null,
            timestamp: null,
            error: 'FEATURE_FLAGS_API_URL_NOT_CONFIGURED',
            promise: null
        };
        syncFeatureFlagsToWindow(featureFlagsCache);
        return getFeatureFlagsSnapshot();
    }

    if (
        featureFlagsCache.promise &&
        featureFlagsCache.clientId === resolvedClientId
    ) {
        await featureFlagsCache.promise;
        return getFeatureFlagsSnapshot();
    }

    if (
        featureFlagsCache.clientId === resolvedClientId &&
        featureFlagsCache.loadedAt &&
        featureFlagsCache.flags &&
        Object.keys(featureFlagsCache.flags).length >= 0 &&
        !featureFlagsCache.error
    ) {
        return getFeatureFlagsSnapshot();
    }

    const requestUrl = `${featureFlagsApiBaseUrl.replace(/\/$/, '')}/feature-flags?client_id=${encodeURIComponent(resolvedClientId)}`;

    const requestPromise = fetch(requestUrl, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json'
        }
    })
        .then(async (response) => {
            if (!response.ok) {
                throw new Error(`Erro ao carregar feature flags: ${response.status}`);
            }

            const payload = await response.json();
            const normalizedPayload = normalizeFeatureFlagsPayload(payload);

            featureFlagsCache = {
                clientId: resolvedClientId,
                loadedAt: new Date().toISOString(),
                flags: normalizedPayload.flags,
                source: normalizedPayload.source,
                timestamp: normalizedPayload.timestamp,
                error: null,
                promise: null
            };

            syncFeatureFlagsToWindow(featureFlagsCache);
            console.log('Feature flags carregadas com sucesso:', featureFlagsCache);
            return getFeatureFlagsSnapshot();
        })
        .catch((error) => {
            console.error('Erro ao inicializar cache de feature flags:', error);

            featureFlagsCache = {
                clientId: resolvedClientId,
                loadedAt: new Date().toISOString(),
                flags: {},
                source: null,
                timestamp: null,
                error: error?.message || 'FEATURE_FLAGS_LOAD_ERROR',
                promise: null
            };

            syncFeatureFlagsToWindow(featureFlagsCache);
            return getFeatureFlagsSnapshot();
        });

    featureFlagsCache = {
        ...featureFlagsCache,
        clientId: resolvedClientId,
        promise: requestPromise
    };

    return await requestPromise;
}


// ─────────────────────────────────────────────────────────────────────────────
// PRÉ-RESOLUÇÃO DE LINKS DO AD (área logada)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Detecta links externos (http/https) no campo `response` de `data` e, quando
 * `origem === 'area_logada'`, substitui cada link pela URL autenticada obtida
 * via ConvertUriToLoginAction — antes de o dado ser entregue ao setMessages.
 *
 * Isso elimina o delay percebido pelo usuário ao clicar, pois a URL autenticada
 * já está embutida no href antes da mensagem aparecer na tela.
 *
 * @param {object} data  - Objeto de resposta da conversation API.
 * @param {string} client_id - ID do cliente.
 * @param {string} origem - Origem da chamada (ex: 'area_logada').
 * @returns {Promise<object>} data mutado com links resolvidos.
 */
async function preResolveAdLinks(data, client_id, origem) {
    // Só aplica na área logada
    if (origem !== 'area_logada') return data;

    const response = data?.response;
    if (typeof response !== 'string') return data;

    // Extrai todos os links http/https da string (texto puro ou dentro de href="...")
    const urlRegex = /https?:\/\/[^\s"'<>)]+/gi;
    const matches = response.match(urlRegex);
    if (!matches || matches.length === 0) return data;

    // Função helper para identificar se uma URL ou mensagem se refere a resultados de exames
    const isResultsLink = (url) => {
        if (!url) return false;
        const lowerUrl = url.toLowerCase();
        const lowerResponse = response.toLowerCase();
        return (
            lowerUrl.includes('resultados') ||
            lowerUrl.includes('resultadosexame') ||
            lowerResponse.includes('para acessar seus resultados')
        );
    };

    // Filtra apenas os links que NÃO são de resultados (links de resultados não devem ser substituídos)
    const linksToResolve = matches.filter(url => !isResultsLink(url));

    if (linksToResolve.length === 0) {
        console.log('ℹ️ preResolveAdLinks: apenas link(s) de resultados detectado(s). Mantendo link(s) original(ais).');
        return data;
    }

    try {
        console.log('🔗 preResolveAdLinks: detectados', linksToResolve.length, 'link(s) a resolver na resposta. Resolvendo antes de exibir...');
        // ConvertUriToLoginAction retorna uma única URL de sessão autenticada
        // (não é parametrizada pelo link de destino), então basta uma chamada.
        const authenticatedUrl = await convertUriToLoginAction(client_id);

        if (!authenticatedUrl) {
            console.warn('⚠️ preResolveAdLinks: não foi possível obter URL autenticada. Links originais mantidos.');
            return data;
        }

        // Substitui apenas os links autorizados pela URL autenticada
        let resolvedResponse = response;
        for (const url of linksToResolve) {
            resolvedResponse = resolvedResponse.split(url).join(authenticatedUrl);
        }

        console.log('✅ preResolveAdLinks: links substituídos pela URL autenticada.');
        data.response = resolvedResponse;
    } catch (err) {
        console.error('❌ preResolveAdLinks: erro ao resolver links, mantendo originais.', err);
    }

    return data;
}

// ─────────────────────────────────────────────────────────────────────────────
// HISTÓRICO DE CONVERSA (últimas 24h)
// -----------------------------------------------------------------------------

/**
 * Verifica se existe uma sessão ativa nas últimas 24h para o client_id.
 * @returns {Promise<{found, resumable, expired, conversationId, session_id}|null>}
 */
export async function checkLatestConversation(client_id, marca, origem) {
    try {
        const baseUrl = getApiBaseUrl();
        const url = `${baseUrl}/conversation/latest?client_id=${encodeURIComponent(client_id)}&marca=${encodeURIComponent(marca)}&origem=${encodeURIComponent(origem)}`;

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                ...getAuthHeader(),
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            console.warn('⚠️ /conversation/latest retornou erro:', response.status);
            return null;
        }

        const data = await response.json();
        console.log('📖 /conversation/latest:', data);
        return data;
    } catch (error) {
        console.error('Erro ao verificar histórico recente:', error);
        return null;
    }
}

/**
 * Recupera o histórico completo de uma sessão e retorna as mensagens formatadas
 * para serem carregadas no chat.
 * @returns {Promise<Array>} Array de mensagens no formato do chat
 */
export async function resumeConversation(client_id, conversation_id, marca, origem) {
    try {
        const baseUrl = getApiBaseUrl();
        const url = `${baseUrl}/conversation/resume?client_id=${encodeURIComponent(client_id)}&conversation_id=${encodeURIComponent(conversation_id)}&marca=${encodeURIComponent(marca)}&origem=${encodeURIComponent(origem)}`;

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                ...getAuthHeader(),
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            console.warn('/conversation/resume retornou erro:', response.status);
            return null;
        }

        const data = await response.json();
        console.log('/conversation/resume recebido — turns:', data?.turns?.length ?? 0);

        if (!data?.turns || !Array.isArray(data.turns) || data.turns.length === 0) {
            return null;
        }

        // Filtrar turns de controle interno (mensagens automáticas internas)
        const INTERNAL_INPUTS = [
            'start',
            'checa-vinculo-exames',
            'consulta-agendamento-finalização',
            'no-exams-received',
            'no-image-received',
            'aguardando dados de pagamento',
            'payment-successful',
        ];

        const filteredTurns = data.turns.filter(turn => {
            const input = (turn.input || '').trim().toLowerCase();
            return !INTERNAL_INPUTS.some(internal => input === internal.toLowerCase());
        });

        if (filteredTurns.length === 0) {
            return null;
        }

        // Remover turns duplicadas ou triplicadas sequenciais ("uma em baixo da outra")
        const deduplicatedTurns = [];
        for (let i = 0; i < filteredTurns.length; i++) {
            const turn = filteredTurns[i];
            const prevTurn = deduplicatedTurns[deduplicatedTurns.length - 1];

            if (prevTurn) {
                const currentInput = (turn.input || '').trim();
                const currentOutput = (turn.output || '').trim();
                const prevInput = (prevTurn.input || '').trim();
                const prevOutput = (prevTurn.output || '').trim();

                if (currentInput === prevInput && currentOutput === prevOutput) {
                    // slot updates
                    prevTurn.context = turn.context;
                    prevTurn.options = turn.options || prevTurn.options;
                    prevTurn.carrossel = turn.carrossel || prevTurn.carrossel;
                    prevTurn.buttonLink = turn.buttonLink || prevTurn.buttonLink;
                    prevTurn.buttonLinkText = turn.buttonLinkText || prevTurn.buttonLinkText;
                    continue;
                }
            }
            deduplicatedTurns.push({ ...turn });
        }

        // Converter turns em mensagens do chat
        const messages = [];

        // Extrair data e exames para o banner de histórico
        const formatDate = (dateVal) => {
            if (!dateVal) return null;
            try {
                const d = new Date(dateVal);
                if (isNaN(d.getTime())) return null;
                const day = String(d.getDate()).padStart(2, '0');
                const month = String(d.getMonth() + 1).padStart(2, '0');
                const year = d.getFullYear();
                return `${day}/${month}/${year}`;
            } catch (e) {
                return null;
            }
        };

        let rawDate = data.updatedAt || data.createdAt || data.date;
        if (!rawDate && data.turns && data.turns.length > 0) {
            for (let i = data.turns.length - 1; i >= 0; i--) {
                const t = data.turns[i];
                if (t.updatedAt || t.createdAt || t.timestamp) {
                    rawDate = t.updatedAt || t.createdAt || t.timestamp;
                    break;
                }
            }
        }
        const formattedDate = formatDate(rawDate) || formatDate(new Date());

        let examesSelecionados = null;
        if (data.context?.slots?.examesSelecionados) {
            examesSelecionados = data.context.slots.examesSelecionados;
        } else {
            for (let i = deduplicatedTurns.length - 1; i >= 0; i--) {
                const turnSlots = deduplicatedTurns[i]?.context?.slots;
                if (turnSlots?.examesSelecionados) {
                    examesSelecionados = turnSlots.examesSelecionados;
                    break;
                }
            }
        }

        let examCount = 0;
        if (examesSelecionados) {
            if (Array.isArray(examesSelecionados.procedimentos)) {
                examCount = examesSelecionados.procedimentos.length;
            } else if (Array.isArray(examesSelecionados)) {
                examCount = examesSelecionados.length;
            } else if (examesSelecionados.procedimentos && typeof examesSelecionados.procedimentos === 'object') {
                examCount = Object.keys(examesSelecionados.procedimentos).length;
            }
        }

        // Banner de histórico recuperado
        messages.push({
            origin: 'bot',
            response: '⏳ <b>Continuando sua conversa anterior</b>',
            options: [],
            bloqueiaInput: false,
            desativaFeedbackPorMensagem: true,
            isHistoryBanner: true,
            historyDate: formattedDate,
            historyExamCount: examCount,
        });

        deduplicatedTurns.forEach((turn, index) => {
            const isLastTurn = index === deduplicatedTurns.length - 1;
            const slots = (isLastTurn && data.context?.slots) ? data.context.slots : (turn.context?.slots || {});

            // Mensagem do usuário
            if (turn.input && turn.input.trim()) {
                messages.push({
                    origin: 'you',
                    response: turn.input.trim(),
                    options: [],
                    isHistory: true,
                });
            }

            // Resposta do bot
            if (turn.output && turn.output.trim()) {
                if (turn.output.includes('$#$')) {
                    const [firstMessage, secondMessage] = turn.output.split('$#$');

                    // Primeira mensagem sem as opções/componentes
                    messages.push({
                        origin: 'bot',
                        response: firstMessage.trim(),
                        options: [],
                        carrossel: [],
                        buttonLink: false,
                        buttonLinkText: '',
                        bloqueiaInput: false,
                        desativaFeedbackPorMensagem: true,
                        isHistory: true,
                    });

                    // Segunda mensagem com as opções/componentes
                    messages.push({
                        origin: 'bot',
                        response: secondMessage.trim(),
                        options: turn.options || (isLastTurn ? slots.options : []) || [],
                        carrossel: turn.carrossel || (isLastTurn ? slots.carrossel : []) || [],
                        buttonLink: turn.buttonLink || (isLastTurn ? slots.buttonLink : false),
                        buttonLinkText: turn.buttonLinkText || (isLastTurn ? slots.buttonLinkText : ''),
                        bloqueiaInput: false,
                        desativaFeedbackPorMensagem: true,
                        isHistory: true,
                        ...(isLastTurn && {
                            componenteConvenio: slots.componenteConvenio || false,
                            componenteRemocaoProcedimentos: slots.componenteRemocaoProcedimentos || false,
                            agendamentoAdicionaProcedimentos: slots.agendamentoAdicionaProcedimentos || false,
                            examesSelecionados: slots.examesSelecionados || null,
                            iniciarPagamento: slots.iniciarPagamento || false,
                            valorPagamento: slots.valorPagamento || null,
                            detalhesOcultos: Array.isArray(slots.detalhesOcultos) ? slots.detalhesOcultos : [],
                            detalhesOcultosTag: slots.detalhesOcultosTag || '',
                            unidadeSelecionadaNome: slots.unidadeSelecionadaNome || null,
                            ocrApenasPedidoMedico: slots.ocrApenasPedidoMedico || false,
                            ocrCarteirinhaNegativo: slots.ocrCarteirinhaNegativo || false,
                            buscaExamesTextual: slots.buscaExamesTextual || false,
                            buscaExamesMovel: slots.buscaExamesMovel || false,
                            agendamentoConvenio: slots.agendamentoConvenio || false,
                            allowVaccines: slots.allowVaccines || false,
                            examesAutorizacao: Array.isArray(slots.examesAutorizacao) ? slots.examesAutorizacao : [],
                            perguntasApoio: (slots.perguntasApoio && slots.perguntasApoio !== "N/A") ? slots.perguntasApoio : null,
                            bloqueiaInputLetras: slots.bloqueiaInputLetras || false
                        })
                    });
                } else {
                    messages.push({
                        origin: 'bot',
                        response: turn.output.trim(),
                        options: turn.options || (isLastTurn ? slots.options : []) || [],
                        carrossel: turn.carrossel || (isLastTurn ? slots.carrossel : []) || [],
                        buttonLink: turn.buttonLink || (isLastTurn ? slots.buttonLink : false),
                        buttonLinkText: turn.buttonLinkText || (isLastTurn ? slots.buttonLinkText : ''),
                        bloqueiaInput: false,
                        desativaFeedbackPorMensagem: true,
                        isHistory: true,
                        ...(isLastTurn && {
                            componenteConvenio: slots.componenteConvenio || false,
                            componenteRemocaoProcedimentos: slots.componenteRemocaoProcedimentos || false,
                            agendamentoAdicionaProcedimentos: slots.agendamentoAdicionaProcedimentos || false,
                            examesSelecionados: slots.examesSelecionados || null,
                            iniciarPagamento: slots.iniciarPagamento || false,
                            valorPagamento: slots.valorPagamento || null,
                            detalhesOcultos: Array.isArray(slots.detalhesOcultos) ? slots.detalhesOcultos : [],
                            detalhesOcultosTag: slots.detalhesOcultosTag || '',
                            unidadeSelecionadaNome: slots.unidadeSelecionadaNome || null,
                            ocrApenasPedidoMedico: slots.ocrApenasPedidoMedico || false,
                            ocrCarteirinhaNegativo: slots.ocrCarteirinhaNegativo || false,
                            buscaExamesTextual: slots.buscaExamesTextual || false,
                            buscaExamesMovel: slots.buscaExamesMovel || false,
                            agendamentoConvenio: slots.agendamentoConvenio || false,
                            allowVaccines: slots.allowVaccines || false,
                            examesAutorizacao: Array.isArray(slots.examesAutorizacao) ? slots.examesAutorizacao : [],
                            perguntasApoio: (slots.perguntasApoio && slots.perguntasApoio !== "N/A") ? slots.perguntasApoio : null,
                            bloqueiaInputLetras: slots.bloqueiaInputLetras || false
                        })
                    });
                }
            }
        });

        return { messages, rawData: data };
    } catch (error) {
        console.error('Erro ao recuperar histórico da conversa:', error);
        return null;
    }
}

/**
 * Obtém o código de autenticação da API
 * @returns {Promise<string>} Código de autenticação
 */
async function getAuthCode() {
    try {
        // Se houver BFF configurado ou no novo fluxo de segurança
        if (process.env.REACT_APP_BFF_API_URL || !process.env.REACT_APP_API_AUTH_URL) {
            return await bffGetAuthCode();
        }

        const response = await fetch(`${process.env.REACT_APP_API_AUTH_URL}/auth/code`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${process.env.REACT_APP_API_CONVERSATION_TOKEN}`
            }
        });

        if (!response.ok) {
            throw new Error(`Erro ao obter código de autenticação: ${response.status}`);
        }

        const data = await response.json();
        return data.code;
    } catch (error) {
        console.error('Erro ao obter código de autenticação:', error);
        throw error;
    }
}

/**
 * Obtém o token de autenticação usando o código
 * @param {string} code Código de autenticação
 * @returns {Promise<Object>} Objeto com token e informações de expiração
 */
async function getAuthToken(code) {
    try {
        // Fluxo Seguro via BFF: O secret nunca fica no frontend
        if (process.env.REACT_APP_BFF_API_URL || !process.env.REACT_APP_AUTH_BASE_SECRET) {
            return await bffGetAuthToken(code);
        }

        const authBaseSecret = process.env.REACT_APP_AUTH_BASE_SECRET || '';
        const fullCode = authBaseSecret + code;

        const response = await fetch(`${process.env.REACT_APP_API_AUTH_URL}/auth/token`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${process.env.REACT_APP_API_CONVERSATION_TOKEN}`
            },
            body: JSON.stringify({ code: fullCode })
        });

        if (!response.ok) {
            throw new Error(`Erro ao obter token: ${response.status}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Erro ao obter token:', error);
        throw error;
    }
}

/**
 * Verifica se o token atual é válido
 * @returns {boolean} True se o token é válido
 */
function isTokenValid() {
    if (!currentToken || !tokenExpiresAt) {
        return false;
    }

    // Verifica se o token expira em menos de 10 minutos (margem de segurança)
    const now = new Date();
    const expirationTime = new Date(tokenExpiresAt);
    const timeUntilExpiration = expirationTime.getTime() - now.getTime();

    return timeUntilExpiration > 600000; // 10 minutos em milissegundos
}

/**
 * Obtém um token válido, renovando se necessário
 * @returns {Promise<string>} Token de autenticação válido
 */
async function getValidToken() {
    if (isTokenValid()) {
        return currentToken;
    }

    try {
        console.log('Token inválido ou expirado, renovando...');
        const code = await getAuthCode();
        const tokenData = await getAuthToken(code);

        currentToken = tokenData.token;
        tokenExpiresAt = tokenData.expiresAt;

        console.log('Token renovado com sucesso');
        return currentToken;
    } catch (error) {
        console.error('Erro ao renovar token:', error);
        throw error;
    }
}

/**
 * Processa resposta de erro de autenticação e renova o token
 * @param {Object} errorData Dados do erro retornado pela API
 * @returns {Promise<string>} Novo token válido
 */
async function handleAuthError(errorData) {
    try {
        console.log('Processando erro de autenticação...');

        if (errorData.authCode && errorData.authCode.code) {
            const code = errorData.authCode.code;
            const tokenData = await getAuthToken(code);
            currentToken = tokenData.token;
            tokenExpiresAt = tokenData.expiresAt;

            console.log('Token renovado após erro de autenticação');
            return currentToken;
        } else {
            // Se não há código no erro, tenta obter um novo código
            return await getValidToken();
        }
    } catch (error) {
        console.error('Erro ao processar erro de autenticação:', error);
        throw error;
    }
}

/**
 * Faz uma requisição autenticada com retry automático em caso de erro de token
 * @param {string} url URL da requisição
 * @param {Object} options Opções da requisição
 * @param {Object} originalData Dados originais para retry (opcional)
 * @returns {Promise<Object>} Resposta da API
 */
export async function authenticatedFetch(url, options, originalData = null) {
    try {
        const authenticatedOptions = {
            ...options,
            headers: {
                ...getAuthHeader(),
                ...(options.headers || {})
            }
        };

        const response = await fetch(url, authenticatedOptions);

        if (!response.ok) {
            const errorText = await response.text();
            let errorData;

            try {
                errorData = JSON.parse(errorText);
            } catch (parseError) {
                throw new Error(`Erro na requisição: ${response.status} ${response.statusText}`);
            }

            // Verificar se é erro de autenticação
            if (errorData.error === 'MISSING_TOKEN' || response.status === 401) {
                console.log('Erro de autenticação detectado, renovando token...');
                const newToken = await handleAuthError(errorData);

                // Retry da requisição com novo token
                const retryOptions = {
                    ...options,
                    headers: {
                        ...options.headers,
                        'Authorization': `Bearer ${newToken}`
                    }
                };

                const retryResponse = await fetch(url, retryOptions);

                if (!retryResponse.ok) {
                    throw new Error(`Erro na requisição após retry: ${retryResponse.status} ${retryResponse.statusText}`);
                }

                return await retryResponse.text().then(text => {
                    if (!text || text.trim() === '') {
                        return {};
                    }
                    try {
                        return JSON.parse(text);
                    } catch (error) {
                        console.error('Erro ao processar resposta JSON:', error);
                        return { response: 'Erro ao processar a resposta do servidor.', options: [] };
                    }
                });
            } else {
                throw new Error(`Erro na requisição: ${response.status} ${response.statusText}`);
            }
        }

        return await response.text().then(text => {
            if (!text || text.trim() === '') {
                return {};
            }
            try {
                return JSON.parse(text);
            } catch (error) {
                console.error('Erro ao processar resposta JSON:', error);
                return { response: 'Erro ao processar a resposta do servidor.', options: [] };
            }
        });

    } catch (error) {
        console.error('Erro na requisição autenticada:', error);
        throw error;
    }
}

export function startChat(messages, setMessages, client_id, marca, origem, operational_system, version_app, localizacao = null, latitude = null, longitude = null, getLocation, requestImage, setRequestId, conversationId = null) {

    const resolvedClientId = client_id || 'web-default';
    const resolvedConversationId = conversationId || (typeof window !== 'undefined' ? window.currentConversationId : '') || resolvedClientId;

    const featureFlagsContext = buildFeatureFlagsContext();

    const bodyContext = {
        context: {
            localizacao,
            operational_system,
            version_app,
            bloqueiaInput: false,
            bloqueiaInputLetras: false,
            csat: false,
            buscaExamesOcr: false,
            buscaExamesTextual: false,
            agendamentoAdicionaProcedimentos: false,
            componenteRemocaoProcedimentos: false,
            ocrApenasPedidoMedico: false,
            componenteConvenio: false,
            buttonLink: false,
            ...featureFlagsContext,
            conversationId: resolvedConversationId,
            conversation_id: resolvedConversationId
        }
    }

    const urlParams = new URLSearchParams(window.location.search);
    const searchParam = urlParams.get('search');

    let message = 'start';
    let requestBody = { 
        message, 
        client_id: resolvedClientId, 
        conversationId: resolvedConversationId,
        conversation_id: resolvedConversationId,
        marca, 
        origem, 
        ...bodyContext, 
        organizationId: getOrganizationId() 
    };
    let externalProviderFlowStarted = false;

    if (searchParam && searchParam.trim() !== '') {
        message = searchParam.trim();
        requestBody = { 
            message, 
            client_id: resolvedClientId, 
            conversationId: resolvedConversationId,
            conversation_id: resolvedConversationId,
            marca, 
            origem, 
            start: true, 
            ...bodyContext, 
            organizationId: getOrganizationId() 
        };

        // Adicionar a mensagem do search na fila como origem 'you'
        const userMessage = { origin: 'you', response: message, options: [] };
        const typing = { origin: 'bot', response: 'Digitando...', options: [], typing: true };
        setMessages([userMessage, typing]);
    }

    authenticatedFetch(`${getApiBaseUrl()}/conversation`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getAuthHeader()
        },
        body: JSON.stringify(requestBody)
    })
        .then(async data => {
            // Pré-resolve links do AD para a URL autenticada antes de exibir ao usuário
            data = await preResolveAdLinks(data, client_id, origem);

            console.log('Resposta da API startChat:', JSON.stringify(data));

            if (data?.externalProviderFlow === true) {
                accelerateWebhookPolling(30000);
                return data;
            }

            // A flag de delegação é suficiente para suprimir a resposta local. O nome
            // do fluxo é metadado e pode vir vazio sem transformar question="" em bolha.
            const providerFlow = data?.externalProviderFlow === true;
            if (providerFlow) {
                externalProviderFlowStarted = true;
                if (searchParam && searchParam.trim() !== '') {
                    accelerateWebhookPolling(30000);
                } else {
                    setMessages(prevMessages => prevMessages.some(message => message.typing)
                        ? prevMessages
                        : [...prevMessages, { origin: 'bot', response: 'Digitando...', options: [], typing: true }]);
                    accelerateWebhookPolling(30000);
                }
                return data;
            }

            // Feature-flag via texto na resposta: quando vier "LIBERAR-VACINAS",
            // liberamos vacinas no componente de busca manual (BuscaExames).
            // Removemos o token do response para não exibir ao usuário.
            try {
                const allowVaccines = typeof data?.response === 'string' && data.response.includes('LIBERAR-VACINAS');
                data.allowVaccines = allowVaccines;
                if (allowVaccines) {
                    data.response = data.response.replace(/LIBERAR-VACINAS/g, '').trim();
                }
            } catch (e) {
                // Não quebrar fluxo por falha de normalização
                data.allowVaccines = false;
            }

            if (data?.requisicaoAgendamento && data?.requisicaoAgendamento !== '') {
                setRequestId(data?.requisicaoAgendamento)
            }

            // Capturar sexo do cliente quando vier do conversation (M/F)
            try {
                const rawSexo = data?.clienteSexo || data?.context?.slots?.clienteSexo;
                if (typeof window !== 'undefined' && typeof rawSexo === 'string') {
                    const sexo = rawSexo.trim().toUpperCase();
                    if (sexo === 'M' || sexo === 'F') {
                        window.currentClienteSexo = sexo;
                    }
                }
            } catch (e) {
                // ignore
            }

            // Capturar idFluxoAgendamento quando vier do conversation
            if (data?.idFluxoAgendamento && String(data.idFluxoAgendamento).trim() !== '') {
                const newIdFluxo = String(data.idFluxoAgendamento).trim();
                try {
                    if (typeof window !== 'undefined') {
                        window.currentIdFluxoAgendamento = newIdFluxo;
                    }
                } catch (e) {
                    // ignore
                }
            }

            // Capturar person/cliente quando vier do conversation (para reutilizar em telas seguintes)
            if (data?.person && typeof data.person === 'object') {
                try {
                    if (typeof window !== 'undefined') {
                        window.currentPerson = data.person;
                        const maybeCustomer = data.person?.customer || data.person?.custumer || null;
                        if (maybeCustomer && typeof maybeCustomer === 'object') {
                            window.currentCustomer = maybeCustomer;
                        }
                    }
                } catch (e) {
                    // ignore
                }
            }
            if (data?.cliente && typeof data.cliente === 'object') {
                try {
                    if (typeof window !== 'undefined') {
                        window.currentCustomer = data.cliente;
                    }
                } catch (e) {
                    // ignore
                }
            }

            // Capturar unidadeSelecionada (para usar como merchantOrderId no pagamento)
            if (data?.unidadeSelecionada && String(data.unidadeSelecionada).trim() !== '') {
                const unidade = String(data.unidadeSelecionada).trim();
                try {
                    if (typeof window !== 'undefined') {
                        window.currentUnidadeSelecionada = unidade;
                    }
                } catch (e) {
                    // ignore
                }
            }

            // Verificar ocrApenasPedidoMedico ANTES das perguntas
            if (data?.ocrApenasPedidoMedico && data?.ocrApenasPedidoMedico === true) {
                console.log('🔍 OCR Pedido Médico detectado (startChat):', {
                    requestId: data?.requisicaoAgendamento,
                    idPessoaFisica: data?.idPessoaFisica
                });

                // Apenas mostrar indicador de carregamento
                setMessages(messages => [...messages, {
                    origin: 'bot',
                    response: 'Digitando...',
                    options: [],
                    request: '',
                    carrossel: [],
                    bloqueiaInput: true,
                    typing: true,
                    isFirstMessage: false
                }]);

                requestImage(data?.requisicaoAgendamento, 'pedido-medico', data?.idPessoaFisica, data?.ocrProvider === 'firstlayer' ? 'firstlayer' : 'fleury')
                setRequestId(data?.requisicaoAgendamento)
                return data; // Interrompe o fluxo, não verifica perguntas
            }

            // Verificar pollingAgendamento e realizar nova chamada automática
            if (data?.desativaFeedbackPorMensagem?.pollingAgendamento === true) {

                const retryBodyContext = {
                    context: {
                        ...bodyContext.context,
                    }
                };

                const pollAgendamento = async () => {
                    try {
                        await new Promise(resolve => setTimeout(resolve, 10000));

                        const retryData = await authenticatedFetch(`${getApiBaseUrl()}/conversation`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                ...getAuthHeader()
                            },
                            body: JSON.stringify({
                                message: 'consulta-agendamento-finalização',
                                client_id: resolvedClientId,
                                conversationId: resolvedConversationId,
                                conversation_id: resolvedConversationId,
                                marca,
                                origem,
                                ...retryBodyContext,
                                start: false,
                                organizationId: process.env.REACT_APP_ORGANIZATION_ID
                            })
                        });

                        console.log('Resposta da API startChat (pollingAgendamento retry):', JSON.stringify(retryData));

                        if (retryData?.requisicaoAgendamento && retryData?.requisicaoAgendamento !== '') {
                            setRequestId(retryData?.requisicaoAgendamento)
                        }

                        if (retryData?.idFluxoAgendamento && String(retryData.idFluxoAgendamento).trim() !== '') {
                            const newIdFluxo = String(retryData.idFluxoAgendamento).trim();
                            try {
                                if (typeof window !== 'undefined') {
                                    window.currentIdFluxoAgendamento = newIdFluxo;
                                }
                            } catch (e) {
                                // ignore
                            }
                        }

                        if (retryData?.person && typeof retryData.person === 'object') {
                            try {
                                if (typeof window !== 'undefined') {
                                    window.currentPerson = retryData.person;
                                    const maybeCustomer = retryData.person?.customer || retryData.person?.custumer || null;
                                    if (maybeCustomer && typeof maybeCustomer === 'object') {
                                        window.currentCustomer = maybeCustomer;
                                    }
                                }
                            } catch (e) {
                                // ignore
                            }
                        }
                        if (retryData?.cliente && typeof retryData.cliente === 'object') {
                            try {
                                if (typeof window !== 'undefined') {
                                    window.currentCustomer = retryData.cliente;
                                }
                            } catch (e) {
                                // ignore
                            }
                        }

                        if (retryData?.unidadeSelecionada && String(retryData.unidadeSelecionada).trim() !== '') {
                            const unidade = String(retryData.unidadeSelecionada).trim();
                            try {
                                if (typeof window !== 'undefined') {
                                    window.currentUnidadeSelecionada = unidade;
                                }
                            } catch (e) {
                                // ignore
                            }
                        }

                        // Capturar sexo do cliente do retryData
                        try {
                            const rawSexo = retryData?.clienteSexo || retryData?.context?.slots?.clienteSexo;
                            if (typeof window !== 'undefined' && typeof rawSexo === 'string') {
                                const sexo = rawSexo.trim().toUpperCase();
                                if (sexo === 'M' || sexo === 'F') {
                                    window.currentClienteSexo = sexo;
                                }
                            }
                        } catch (e) {
                            // ignore
                        }

                        if (retryData?.desativaFeedbackPorMensagem?.pollingAgendamento === true) {
                            return await pollAgendamento();
                        }

                        return retryData;
                    } catch (retryError) {
                        console.error('Erro na chamada de pollingAgendamento no startChat:', retryError);
                        return data;
                    }
                };

                return pollAgendamento();
            }

            if (
                data?.perguntasApoio === "N/A" &&
                (!data?.options || data?.options.length === 0) &&
                (
                    (data?.bloqueiaInput !== true && data?.respostaExtensa !== true) ||
                    (
                        typeof data?.response === 'string' &&
                        (
                            data?.response.includes('Exames vinculados com sucesso!') ||
                            data?.response.includes('Procedimentos vinculados com sucesso!')
                        )
                    )
                )
            ) {
                console.log('perguntasApoio retornou N/A e options está vazio no startChat, fazendo nova chamada silenciosa...');

                const retryBodyContext = {
                    context: {
                        ...bodyContext.context,
                    }
                };

                return authenticatedFetch(`${getApiBaseUrl()}/conversation`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ 
                        message: message, 
                        client_id: resolvedClientId, 
                        conversationId: resolvedConversationId,
                        conversation_id: resolvedConversationId,
                        marca, 
                        origem, 
                        ...retryBodyContext, 
                        start: searchParam && searchParam.trim() !== '' ? true : false, 
                        organizationId: getOrganizationId() 
                    })
                }).then(retryData => {
                    console.log('Resposta da API startChat (retry):', JSON.stringify(retryData));

                    // Atualizar requestId se necessário
                    if (retryData?.requisicaoAgendamento && retryData?.requisicaoAgendamento !== '') {
                        setRequestId(retryData?.requisicaoAgendamento)
                    }

                    if (retryData?.idFluxoAgendamento && String(retryData.idFluxoAgendamento).trim() !== '') {
                        const newIdFluxo = String(retryData.idFluxoAgendamento).trim();
                        try {
                            if (typeof window !== 'undefined') {
                                window.currentIdFluxoAgendamento = newIdFluxo;
                            }
                        } catch (e) {
                            // ignore
                        }
                    }

                    if (retryData?.person && typeof retryData.person === 'object') {
                        try {
                            if (typeof window !== 'undefined') {
                                window.currentPerson = retryData.person;
                                const maybeCustomer = retryData.person?.customer || retryData.person?.custumer || null;
                                if (maybeCustomer && typeof maybeCustomer === 'object') {
                                    window.currentCustomer = maybeCustomer;
                                }
                            }
                        } catch (e) {
                            // ignore
                        }
                    }
                    if (retryData?.cliente && typeof retryData.cliente === 'object') {
                        try {
                            if (typeof window !== 'undefined') {
                                window.currentCustomer = retryData.cliente;
                            }
                        } catch (e) {
                            // ignore
                        }
                    }

                    if (retryData?.unidadeSelecionada && String(retryData.unidadeSelecionada).trim() !== '') {
                        const unidade = String(retryData.unidadeSelecionada).trim();
                        try {
                            if (typeof window !== 'undefined') {
                                window.currentUnidadeSelecionada = unidade;
                            }
                        } catch (e) {
                            // ignore
                        }
                    }

                    // Capturar sexo do cliente do retryData
                    try {
                        const rawSexo = retryData?.clienteSexo || retryData?.context?.slots?.clienteSexo;
                        if (typeof window !== 'undefined' && typeof rawSexo === 'string') {
                            const sexo = rawSexo.trim().toUpperCase();
                            if (sexo === 'M' || sexo === 'F') {
                                window.currentClienteSexo = sexo;
                            }
                        }
                    } catch (e) {
                        // ignore
                    }

                    // Usar os dados da segunda chamada
                    return retryData;
                }).catch(retryError => {
                    console.error('Erro na chamada de retry no startChat:', retryError);
                    // Em caso de erro no retry, usar os dados originais
                    return data;
                });
            }

            // Se não for N/A, continuar com os dados normais
            return data;
        })
        .then(data => {
            if (data?.externalProviderFlow === true) {
                return;
            }

            // Se for ocrApenasPedidoMedico, não processar mensagens normais
            if (data?.ocrApenasPedidoMedico && data?.ocrApenasPedidoMedico === true) {
                console.log('🔍 Pulando processamento de mensagens para OCR Pedido Médico (startChat)');
                return; // Sai do fluxo sem processar mensagens
            }

            if (data?.getLoc && data?.getLoc !== false) {
                if (((localizacao !== null) && (latitude !== '' && longitude !== '') || ((localizacao == null) && (latitude !== '' && longitude !== '')))) {
                    getLocation()
                    if (searchParam && searchParam.trim() !== '') {
                        // Se foi busca via URL, remover o typing indicator
                        setMessages(messages => messages.slice(0, -1));
                    }
                } else {
                    getLocation()
                    if (searchParam && searchParam.trim() !== '') {
                        // Se foi busca via URL, remover o typing indicator
                        setMessages(messages => messages.slice(0, -1));
                    }
                }
            }

            if (data?.getLoc && data?.getLoc === true) {

                getLocation()

            } else if ((data?.ocrCarteirinhaNegativo && data?.ocrCarteirinhaNegativo === true) && (data?.componenteConvenio === true || data?.desativaFeedbackPorMensagem?.componenteConvenio === true)) {
                // OCR carteirinha com resultado negativo: abrir componente de convênio manualmente
                const preselectedId = (typeof window !== 'undefined' && window.currentInsuranceSelected && window.currentInsuranceSelected.insurance && window.currentInsuranceSelected.insurance.id) ? String(window.currentInsuranceSelected.insurance.id) : '';
                if (searchParam && searchParam.trim() !== '') {
                    setMessages(messages => messages.slice(0, -1));
                }
                setMessages([{
                    origin: 'bot',
                    response: data?.response || 'Por favor, informe os dados do seu convênio.',
                    options: [],
                    buttonLink: data.buttonLink === true,
                    buttonLinkText: data.buttonLinkText || '',
                    componenteConvenio: true,
                    preselectedInsuranceId: preselectedId,
                    forceManualConvenioFlow: true,
                    desativaFeedbackPorMensagem: { desativaFeedbackPorMensagem: true, componenteConvenio: true }
                }]);

            } else if (data?.ocrCarteirinha && data?.ocrCarteirinha === true) {
                // Verificar se OCR de carteirinha está habilitado via variável de ambiente
                const ocrCarteirinhaEnabled = process.env.REACT_APP_OCR_CARTEIRINHA_ENABLED !== 'false';

                if (ocrCarteirinhaEnabled) {
                    requestImage(data?.requisicaoAgendamento, 'ocr-carteirinha', data?.idPessoaFisica, data?.ocrProvider === 'firstlayer' ? 'firstlayer' : 'fleury')
                    setRequestId(data?.requisicaoAgendamento)
                } else {
                    const preselectedId = (typeof window !== 'undefined' && window.currentInsuranceSelected && window.currentInsuranceSelected.insurance && window.currentInsuranceSelected.insurance.id) ? String(window.currentInsuranceSelected.insurance.id) : '';
                    // OCR desabilitado: abrir componente de convênio manualmente
                    if (searchParam && searchParam.trim() !== '') {
                        setMessages(messages => messages.slice(0, -1));
                    }
                    setMessages([{
                        origin: 'bot',
                        response: data?.response || 'Por favor, selecione seu convênio, empresa e plano.',
                        options: [],
                        buttonLink: data.buttonLink === true,
                        buttonLinkText: data.buttonLinkText || '',
                        componenteConvenio: true,
                        preselectedInsuranceId: preselectedId,
                        forceManualConvenioFlow: false,
                        desativaFeedbackPorMensagem: { desativaFeedbackPorMensagem: true, componenteConvenio: true }
                    }]);
                }

            } else if (data?.buscaExamesOcr && data?.buscaExamesOcr === true && !(data?.ocrApenasPedidoMedico && data?.ocrApenasPedidoMedico === true)) {

                requestImage(data?.requisicaoAgendamento, 'normal', data?.idPessoaFisica)
                setRequestId(data?.requisicaoAgendamento)

            } else {
                // Nunca renderizar a resposta vazia de uma delegação externa.
                // Esta proteção cobre ramos que adicionem handlers acima deste ponto.
                if (externalProviderFlowStarted) {
                    return data;
                }

                if (data.response && data.response.includes('$#$')) {
                    const [firstMessage, secondMessage] = data.response.split('$#$');

                    if (searchParam && searchParam.trim() !== '') {
                        // Se foi uma busca via URL, usar o padrão de messagesWithoutTyping
                        setMessages(prevMessages => {
                            const messagesWithoutTyping = prevMessages.slice(0, -1);
                            return [...messagesWithoutTyping,
                            {
                                origin: 'bot',
                                response: firstMessage.trim(),
                                options: [],
                                request: data.request || '',
                                carrossel: null,
                                bloqueiaInput: false,
                                bloqueiaInputLetras: data.bloqueiaInputLetras || data.context?.slots?.bloqueiaInputLetras || data.slots?.bloqueiaInputLetras || false,
                                inputReclamacao: false,
                                inputElogio: false,
                                inputSugestao: false,
                                isFirstMessage: true,
                                autoservice: data.autosservico || "",
                                buscaExamesTextual: data.buscaExamesTextual || false,
                                buscaExamesMovel: data.buscaExamesMovel || false,
                                agendamentoConvenio: data.agendamentoConvenio || false,
                                allowVaccines: data.allowVaccines || false,
                                componenteRemocaoProcedimentos: data.componenteRemocaoProcedimentos || false,
                                examesSelecionados: data.examesSelecionados || null,
                                desativaFeedbackPorMensagem: (typeof data?.desativaFeedbackPorMensagem === 'object' ? data.desativaFeedbackPorMensagem : (data?.desativaFeedbackPorMensagem === true || data?.desativaFeedbackPorMensagem?.desativaFeedbackPorMensagem === true)),
                                perguntasApoio: null, // Primeira mensagem não terá perguntas
                                agendamentoAdicionaProcedimentos: data.agendamentoAdicionaProcedimentos || false,
                                respostaExtensa: data.respostaExtensa || false,
                                componenteConvenio: data.componenteConvenio || false,
                                ocrCarteirinhaNegativo: data.ocrCarteirinhaNegativo || false,
                                iniciarPagamento: data.iniciarPagamento || false,
                                valorPagamento: data.valorPagamento ?? null,
                                detalhesOcultos: Array.isArray(data.detalhesOcultos) ? data.detalhesOcultos : [],
                                detalhesOcultosTag: data.detalhesOcultosTag || '',
                                unidadeSelecionadaNome: data.unidadeSelecionadaNome || null
                            },
                            {
                                origin: 'bot',
                                response: secondMessage.trim(),
                                options: data.options || [],
                                request: data.request || '',
                                carrossel: data.carrossel || null,
                                bloqueiaInput: data.bloqueiaInput || false,
                                bloqueiaInputLetras: data.bloqueiaInputLetras || data.context?.slots?.bloqueiaInputLetras || data.slots?.bloqueiaInputLetras || false,
                                inputReclamacao: false,
                                inputElogio: false,
                                inputSugestao: false,
                                isFirstMessage: false,
                                csat: data.csat || false,
                                autoservice: data.autosservico || "",
                                buscaExamesTextual: data.buscaExamesTextual || false,
                                buscaExamesMovel: data.buscaExamesMovel || false,
                                agendamentoConvenio: data.agendamentoConvenio || false,
                                allowVaccines: data.allowVaccines || false,
                                componenteRemocaoProcedimentos: data.componenteRemocaoProcedimentos || false,
                                examesSelecionados: data.examesSelecionados || null,
                                desativaFeedbackPorMensagem: (typeof data?.desativaFeedbackPorMensagem === 'object' ? data.desativaFeedbackPorMensagem : (data?.desativaFeedbackPorMensagem === true || data?.desativaFeedbackPorMensagem?.desativaFeedbackPorMensagem === true)),
                                perguntasApoio: (data.perguntasApoio && data.perguntasApoio !== "N/A") ? data.perguntasApoio : null, // Segunda mensagem pode ter perguntas
                                agendamentoAdicionaProcedimentos: data.agendamentoAdicionaProcedimentos || false,
                                respostaExtensa: data.respostaExtensa || false,
                                componenteConvenio: data.componenteConvenio || false,
                                ocrCarteirinhaNegativo: data.ocrCarteirinhaNegativo || false,
                                iniciarPagamento: data.iniciarPagamento || false,
                                valorPagamento: data.valorPagamento ?? null,
                                detalhesOcultos: Array.isArray(data.detalhesOcultos) ? data.detalhesOcultos : [],
                                detalhesOcultosTag: data.detalhesOcultosTag || '',
                                unidadeSelecionadaNome: data.unidadeSelecionadaNome || null
                            }
                            ];
                        });
                    } else {
                        // Fluxo normal sem search param
                        setMessages([
                            {
                                origin: 'bot',
                                response: firstMessage.trim(),
                                options: [],
                                request: data.request || '',
                                carrossel: null,
                                bloqueiaInput: false,
                                bloqueiaInputLetras: data.bloqueiaInputLetras || data.context?.slots?.bloqueiaInputLetras || data.slots?.bloqueiaInputLetras || false,
                                inputReclamacao: false,
                                inputElogio: false,
                                inputSugestao: false,
                                isFirstMessage: true,
                                autoservice: data.autosservico || "",
                                buscaExamesTextual: data.buscaExamesTextual || false,
                                buscaExamesMovel: data.buscaExamesMovel || false,
                                agendamentoConvenio: data.agendamentoConvenio || false,
                                allowVaccines: data.allowVaccines || false,
                                buttonLink: data.buttonLink || false,
                                buttonLinkText: data.buttonLinkText || '',
                                componenteRemocaoProcedimentos: data.componenteRemocaoProcedimentos || false,
                                examesSelecionados: data.examesSelecionados || null,
                                desativaFeedbackPorMensagem: (typeof data?.desativaFeedbackPorMensagem === 'object' ? data.desativaFeedbackPorMensagem : (data?.desativaFeedbackPorMensagem === true || data?.desativaFeedbackPorMensagem?.desativaFeedbackPorMensagem === true)),
                                perguntasApoio: null, // Primeira mensagem não terá perguntas
                                agendamentoAdicionaProcedimentos: data.agendamentoAdicionaProcedimentos || false,
                                componenteConvenio: data.componenteConvenio || false,
                                ocrCarteirinhaNegativo: data.ocrCarteirinhaNegativo || false,
                                iniciarPagamento: data.iniciarPagamento || false,
                                valorPagamento: data.valorPagamento ?? null,
                                detalhesOcultos: Array.isArray(data.detalhesOcultos) ? data.detalhesOcultos : [],
                                detalhesOcultosTag: data.detalhesOcultosTag || '',
                                unidadeSelecionadaNome: data.unidadeSelecionadaNome || null
                            },
                            {
                                origin: 'bot',
                                response: secondMessage.trim(),
                                options: data.options || [],
                                request: data.request || '',
                                carrossel: data.carrossel || null,
                                bloqueiaInput: data.bloqueiaInput || false,
                                bloqueiaInputLetras: data.bloqueiaInputLetras || data.context?.slots?.bloqueiaInputLetras || data.slots?.bloqueiaInputLetras || false,
                                inputReclamacao: data.inputReclamacao || false,
                                inputElogio: data.inputElogio || false,
                                inputSugestao: data.inputSugestao || false,
                                isFirstMessage: false,
                                csat: data.csat || false,
                                autoservice: data.autosservico || "",
                                buscaExamesTextual: data.buscaExamesTextual || false,
                                buscaExamesMovel: data.buscaExamesMovel || false,
                                agendamentoConvenio: data.agendamentoConvenio || false,
                                allowVaccines: data.allowVaccines || false,
                                buttonLink: data.buttonLink || false,
                                buttonLinkText: data.buttonLinkText || '',
                                componenteRemocaoProcedimentos: data.componenteRemocaoProcedimentos || false,
                                examesSelecionados: data.examesSelecionados || null,
                                desativaFeedbackPorMensagem: (typeof data?.desativaFeedbackPorMensagem === 'object' ? data.desativaFeedbackPorMensagem : (data?.desativaFeedbackPorMensagem === true || data?.desativaFeedbackPorMensagem?.desativaFeedbackPorMensagem === true)),
                                perguntasApoio: (data.perguntasApoio && data.perguntasApoio !== "N/A") ? data.perguntasApoio : null, // Segunda mensagem pode ter perguntas
                                agendamentoAdicionaProcedimentos: data.agendamentoAdicionaProcedimentos || false,
                                respostaExtensa: data.respostaExtensa || false,
                                componenteConvenio: data.componenteConvenio || false,
                                ocrCarteirinhaNegativo: data.ocrCarteirinhaNegativo || false,
                                iniciarPagamento: data.iniciarPagamento || false,
                                valorPagamento: data.valorPagamento ?? null,
                                detalhesOcultos: Array.isArray(data.detalhesOcultos) ? data.detalhesOcultos : [],
                                detalhesOcultosTag: data.detalhesOcultosTag || '',
                                unidadeSelecionadaNome: data.unidadeSelecionadaNome || null
                            }
                        ]);
                    }
                } else {
                    if (searchParam && searchParam.trim() !== '') {
                        // Se foi uma busca via URL, usar o padrão de messagesWithoutTyping
                        setMessages(prevMessages => {
                            const messagesWithoutTyping = prevMessages.slice(0, -1);
                            return [...messagesWithoutTyping, {
                                origin: 'bot',
                                response: data.response,
                                options: data.options || [],
                                request: data.request || '',
                                carrossel: data.carrossel || null,
                                bloqueiaInput: data.bloqueiaInput || false,
                                bloqueiaInputLetras: data.bloqueiaInputLetras || data.context?.slots?.bloqueiaInputLetras || data.slots?.bloqueiaInputLetras || false,
                                inputReclamacao: data.inputReclamacao || false,
                                inputElogio: data.inputElogio || false,
                                inputSugestao: data.inputSugestao || false,
                                csat: data.csat || false,
                                autoservice: data.autosservico || "",
                                buscaExamesTextual: data.buscaExamesTextual || false,
                                buscaExamesMovel: data.buscaExamesMovel || false,
                                agendamentoConvenio: data.agendamentoConvenio || false,
                                allowVaccines: data.allowVaccines || false,
                                componenteRemocaoProcedimentos: data.componenteRemocaoProcedimentos || false,
                                examesSelecionados: data.examesSelecionados || null,
                                desativaFeedbackPorMensagem: (typeof data?.desativaFeedbackPorMensagem === 'object' ? data.desativaFeedbackPorMensagem : (data?.desativaFeedbackPorMensagem === true || data?.desativaFeedbackPorMensagem?.desativaFeedbackPorMensagem === true)),
                                perguntasApoio: (data.perguntasApoio && data.perguntasApoio !== "N/A") ? data.perguntasApoio : null,
                                agendamentoAdicionaProcedimentos: data.agendamentoAdicionaProcedimentos || false,
                                respostaExtensa: data.respostaExtensa || false,
                                componenteConvenio: data.componenteConvenio || false,
                                ocrCarteirinhaNegativo: data.ocrCarteirinhaNegativo || false,
                                iniciarPagamento: data.iniciarPagamento || false,
                                valorPagamento: data.valorPagamento ?? null,
                                detalhesOcultos: Array.isArray(data.detalhesOcultos) ? data.detalhesOcultos : [],
                                detalhesOcultosTag: data.detalhesOcultosTag || '',
                                unidadeSelecionadaNome: data.unidadeSelecionadaNome || null
                            }];
                        });
                    } else {
                        // Fluxo normal para início sem parâmetro search
                        setMessages([{
                            origin: 'bot',
                            response: data.response,
                            options: data.options || [],
                            request: data.request || '',
                            carrossel: data.carrossel || null,
                            bloqueiaInput: data.bloqueiaInput || false,
                            bloqueiaInputLetras: data.bloqueiaInputLetras || data.context?.slots?.bloqueiaInputLetras || data.slots?.bloqueiaInputLetras || false,
                            inputReclamacao: data.inputReclamacao || false,
                            inputElogio: data.inputElogio || false,
                            inputSugestao: data.inputSugestao || false,
                            csat: data.csat || false,
                            autoservice: data.autosservico || "",
                            buscaExamesTextual: data.buscaExamesTextual || false,
                            buscaExamesMovel: data.buscaExamesMovel || false,
                            agendamentoConvenio: data.agendamentoConvenio || false,
                            allowVaccines: data.allowVaccines || false,
                            componenteRemocaoProcedimentos: data.componenteRemocaoProcedimentos || false,
                            examesSelecionados: data.examesSelecionados || null,
                            desativaFeedbackPorMensagem: (typeof data?.desativaFeedbackPorMensagem === 'object' ? data.desativaFeedbackPorMensagem : (data?.desativaFeedbackPorMensagem === true || data?.desativaFeedbackPorMensagem?.desativaFeedbackPorMensagem === true)),
                            perguntasApoio: (data.perguntasApoio && data.perguntasApoio !== "N/A") ? data.perguntasApoio : null,
                            agendamentoAdicionaProcedimentos: data.agendamentoAdicionaProcedimentos || false,
                            respostaExtensa: data.respostaExtensa || false,
                            componenteConvenio: data.componenteConvenio || false,
                            ocrCarteirinhaNegativo: data.ocrCarteirinhaNegativo || false,
                            iniciarPagamento: data.iniciarPagamento || false,
                            valorPagamento: data.valorPagamento ?? null,
                            detalhesOcultos: Array.isArray(data.detalhesOcultos) ? data.detalhesOcultos : [],
                            detalhesOcultosTag: data.detalhesOcultosTag || '',
                            unidadeSelecionadaNome: data.unidadeSelecionadaNome || null
                        }]);
                    }
                }
            }
        })
        .catch(error => {
            console.error('Erro na requisição de start:', error);

            if (searchParam && searchParam.trim() !== '') {
                // Se foi uma busca via URL, remover o typing indicator e mostrar erro
                setMessages(prevMessages => {
                    const messagesWithoutTyping = prevMessages.slice(0, -1);
                    return [...messagesWithoutTyping, {
                        origin: 'bot',
                        response: 'Desculpe, ocorreu um erro na comunicação com o servidor. Por favor, tente novamente mais tarde.',
                        options: [],
                        request: '',
                        carrossel: null,
                        bloqueiaInput: false,
                        desativaFeedbackPorMensagem: true, // Desativar feedback para mensagens de erro
                        agendamentoAdicionaProcedimentos: false,
                        componenteConvenio: false,
                        ocrCarteirinhaNegativo: false
                    }];
                });
            } else {
                // Fluxo normal de erro
                setMessages([...messages, {
                    origin: 'bot',
                    response: 'Desculpe, ocorreu um erro na comunicação com o servidor. Por favor, tente novamente mais tarde.',
                    options: [],
                    request: '',
                    carrossel: null,
                    bloqueiaInput: false,
                    desativaFeedbackPorMensagem: true, // Desativar feedback para mensagens de erro
                    agendamentoAdicionaProcedimentos: false,
                    componenteConvenio: false,
                    ocrCarteirinhaNegativo: false
                }]);
            }
        });
}

export function sendMessage(setMessages, client_id, message, messages, marca, origem, teste, localizacao, latitude = null, longitude = null, lastInput = null, setTeste, getLocation, operational_system, version_app, examesSelecionados = null, additionalContext = null, requestImage, setRequestId, insuranceSelected = null) {

    console.log('🔵 ========== SENDMESSAGE INICIADO ==========');
    console.log('📥 insuranceSelected recebido:');
    console.log('   Tipo:', typeof insuranceSelected);
    console.log('   É null?', insuranceSelected === null);
    console.log('   É undefined?', insuranceSelected === undefined);
    if (insuranceSelected) {
        console.log('   JSON:', JSON.stringify(insuranceSelected, null, 2));
        console.log('   insurance.id:', insuranceSelected.insurance?.id);
        console.log('   company.id:', insuranceSelected.company?.id);
        console.log('   plan.id:', insuranceSelected.plan?.id);
    }
    console.log('============================================');

    // Determinar se o preenchimento da carteirinha foi manual (se insuranceSelected estiver presente)
    let carterinhaManualVal = undefined;
    if (insuranceSelected && typeof insuranceSelected.isManual === 'boolean') {
        carterinhaManualVal = insuranceSelected.isManual;
        if (typeof window !== 'undefined') {
            window.currentCarterinhaManual = insuranceSelected.isManual;
        }
    } else if (typeof window !== 'undefined' && window.currentCarterinhaManual !== undefined) {
        carterinhaManualVal = window.currentCarterinhaManual;
    }

    // Resolve client_id and conversation_id early
    const resolvedClientId = (typeof window !== 'undefined' ? window.currentClientId : '') || client_id;
    const resolvedConversationId =
        additionalContext?.conversation_id ||
        (typeof window !== 'undefined' ? window.currentConversationId : '') ||
        client_id;

    // Transforme o array de mensagens em um objeto JSON
    // Faça a chamada de API usando o método fetch
    const featureFlagsContext = buildFeatureFlagsContext();
    const bodyContext = {
        context: {
            localizacao,
            operational_system,
            version_app,
            bloqueiaInput: false,
            bloqueiaInputLetras: false,
            csat: false,
            buscaExamesOcr: false,
            buscaExamesTextual: false,
            agendamentoAdicionaProcedimentos: false,
            componenteRemocaoProcedimentos: false,
            ocrApenasPedidoMedico: false,
            componenteConvenio: false,
            ocrCarteirinha: false,
            pollingBindExams: false,
            buttonLink: false,
            ...featureFlagsContext,
            conversationId: resolvedConversationId,
            conversation_id: resolvedConversationId,

            ...(carterinhaManualVal !== undefined && { 'carterinha-manual': carterinhaManualVal }),
            ...(insuranceSelected && { insuranceSelected }), // Adicionar insuranceSelected ao contexto se fornecido
            ...(examesSelecionados && { examesSelecionados }), // Adicionar exames selecionados ao contexto se fornecido
            ...(additionalContext?.context || {}) // Adicionar contexto adicional se fornecido
        }
    }

    console.log('📦 bodyContext construído:');
    console.log('   insuranceSelected no context?', !!bodyContext.context.insuranceSelected);
    if (bodyContext.context.insuranceSelected) {
        console.log('   JSON:', JSON.stringify(bodyContext.context.insuranceSelected, null, 2));
    }

    try {
        if (insuranceSelected && typeof window !== 'undefined') {
            window.currentInsuranceSelected = insuranceSelected;
            console.log('✅ insuranceSelected salvo em window.currentInsuranceSelected');
        }
    } catch (e) {
        console.error('❌ Erro ao salvar insuranceSelected:', e);
    }

    return authenticatedFetch(`${getApiBaseUrl()}/conversation`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getAuthHeader()
        },
        body: JSON.stringify({
            message: message,
            client_id: resolvedClientId,
            conversation_id: resolvedConversationId,
            conversationId: resolvedConversationId,
            marca,
            origem,
            ...bodyContext,
            ...(additionalContext?.isButton === true && additionalContext?.button === message
                ? { button: message } : {}),
            ...(additionalContext?.attachment ? { attachment: additionalContext.attachment } : {}),
            lastInput: lastInput,
            start: false,
            organizationId: getOrganizationId()
        })
    })
        .then(async data => {
            // Pré-resolve links do AD para a URL autenticada antes de exibir ao usuário
            data = await preResolveAdLinks(data, resolvedClientId, origem);

            if (additionalContext?.attachment) {
                console.log('Resposta da API sendMessage: attachment dispatched');
            } else {
                console.log('Resposta da API sendMessage:', JSON.stringify(data));
            }
            console.log('🔑 Verificando requisicaoAgendamento na resposta:', data?.requisicaoAgendamento);

            const providerFlow = data?.externalProviderFlow === true;
            if (providerFlow) {
                accelerateWebhookPolling(30000);
                return data;
            }

            // Feature-flag via texto na resposta: quando vier "LIBERAR-VACINAS",
            // liberamos vacinas no componente de busca manual (BuscaExames).
            // Removemos o token do response para não exibir ao usuário.
            try {
                const allowVaccines = typeof data?.response === 'string' && data.response.includes('LIBERAR-VACINAS');
                data.allowVaccines = allowVaccines;
                if (allowVaccines) {
                    data.response = data.response.replace(/LIBERAR-VACINAS/g, '').trim();
                }
            } catch (e) {
                data.allowVaccines = false;
            }

            // SEMPRE atualizar requestId se vier na resposta
            if (data?.requisicaoAgendamento && data?.requisicaoAgendamento !== '') {
                const newRequestId = String(data.requisicaoAgendamento);
                console.log('✅ Atualizando requestId:', newRequestId);
                setRequestId(newRequestId);

                // Atualizar também no window para garantir que está disponível globalmente
                if (typeof window !== 'undefined') {
                    window.currentRequestId = newRequestId;
                }
            }

            // Capturar sexo do cliente quando vier do conversation (M/F)
            try {
                const rawSexo = data?.clienteSexo || data?.context?.slots?.clienteSexo;
                if (typeof window !== 'undefined' && typeof rawSexo === 'string') {
                    const sexo = rawSexo.trim().toUpperCase();
                    if (sexo === 'M' || sexo === 'F') {
                        window.currentClienteSexo = sexo;
                    }
                }
            } catch (e) {
                // ignore
            }

            // Capturar idFluxoAgendamento quando vier do conversation
            if (data?.idFluxoAgendamento && String(data.idFluxoAgendamento).trim() !== '') {
                const newIdFluxo = String(data.idFluxoAgendamento).trim();
                try {
                    if (typeof window !== 'undefined') {
                        window.currentIdFluxoAgendamento = newIdFluxo;
                    }
                } catch (e) {
                    // ignore
                }
            }

            // Capturar person/cliente quando vier do conversation (para reutilizar em telas seguintes)
            if (data?.person && typeof data.person === 'object') {
                try {
                    if (typeof window !== 'undefined') {
                        window.currentPerson = data.person;
                        const maybeCustomer = data.person?.customer || data.person?.custumer || null;
                        if (maybeCustomer && typeof maybeCustomer === 'object') {
                            window.currentCustomer = maybeCustomer;
                        }
                    }
                } catch (e) {
                    // ignore
                }
            }
            if (data?.cliente && typeof data.cliente === 'object') {
                try {
                    if (typeof window !== 'undefined') {
                        window.currentCustomer = data.cliente;
                    }
                } catch (e) {
                    // ignore
                }
            }

            // Capturar unidadeSelecionada (para usar como merchantOrderId no pagamento)
            if (data?.unidadeSelecionada && String(data.unidadeSelecionada).trim() !== '') {
                const unidade = String(data.unidadeSelecionada).trim();
                try {
                    if (typeof window !== 'undefined') {
                        window.currentUnidadeSelecionada = unidade;
                    }
                } catch (e) {
                    // ignore
                }
            }

            // Verificar ocrApenasPedidoMedico ANTES das perguntas
            if (data?.ocrApenasPedidoMedico && data?.ocrApenasPedidoMedico === true) {
                console.log('🖼️ ocrApenasPedidoMedico detectado, solicitando imagem...');
                const reqId = data?.requisicaoAgendamento;
                if (reqId && reqId !== '') {
                    setRequestId(reqId);
                }
                requestImage(reqId, 'pedido-medico', data?.idPessoaFisica, data?.ocrProvider === 'firstlayer' ? 'firstlayer' : 'fleury');
                return data;
            }

            // Verificar pollingAgendamento e realizar nova chamada automática
            if (data?.desativaFeedbackPorMensagem?.pollingAgendamento === true) {

                const retryBodyContext = {
                    context: {
                        ...bodyContext.context,
                    }
                };

                const pollAgendamento = async () => {
                    try {
                        await new Promise(resolve => setTimeout(resolve, 10000));

                        const retryData = await authenticatedFetch(`${getApiBaseUrl()}/conversation`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                ...getAuthHeader()
                            },
                            body: JSON.stringify({
                                message: 'consulta-agendamento-finalização',
                                client_id: resolvedClientId,
                                conversationId: resolvedConversationId,
                                conversation_id: resolvedConversationId,
                                marca,
                                origem,
                                ...retryBodyContext,
                                lastInput: lastInput,
                                start: false,
                                organizationId: getOrganizationId()
                            })
                        });

                        console.log('Resposta da API sendMessage (pollingAgendamento retry):', JSON.stringify(retryData));

                        if (retryData?.requisicaoAgendamento && retryData?.requisicaoAgendamento !== '') {
                            setRequestId(retryData?.requisicaoAgendamento)
                        }

                        if (retryData?.idFluxoAgendamento && String(retryData.idFluxoAgendamento).trim() !== '') {
                            const newIdFluxo = String(retryData.idFluxoAgendamento).trim();
                            try {
                                if (typeof window !== 'undefined') {
                                    window.currentIdFluxoAgendamento = newIdFluxo;
                                }
                            } catch (e) {
                                // ignore
                            }
                        }

                        if (retryData?.person && typeof retryData.person === 'object') {
                            try {
                                if (typeof window !== 'undefined') {
                                    window.currentPerson = retryData.person;
                                    const maybeCustomer = retryData.person?.customer || retryData.person?.custumer || null;
                                    if (maybeCustomer && typeof maybeCustomer === 'object') {
                                        window.currentCustomer = maybeCustomer;
                                    }
                                }
                            } catch (e) {
                                // ignore
                            }
                        }
                        if (retryData?.cliente && typeof retryData.cliente === 'object') {
                            try {
                                if (typeof window !== 'undefined') {
                                    window.currentCustomer = retryData.cliente;
                                }
                            } catch (e) {
                                // ignore
                            }
                        }

                        if (retryData?.unidadeSelecionada && String(retryData.unidadeSelecionada).trim() !== '') {
                            const unidade = String(retryData.unidadeSelecionada).trim();
                            try {
                                if (typeof window !== 'undefined') {
                                    window.currentUnidadeSelecionada = unidade;
                                }
                            } catch (e) {
                                // ignore
                            }
                        }

                        // Capturar sexo do cliente do retryData
                        try {
                            const rawSexo = retryData?.clienteSexo || retryData?.context?.slots?.clienteSexo;
                            if (typeof window !== 'undefined' && typeof rawSexo === 'string') {
                                const sexo = rawSexo.trim().toUpperCase();
                                if (sexo === 'M' || sexo === 'F') {
                                    window.currentClienteSexo = sexo;
                                }
                            }
                        } catch (e) {
                            // ignore
                        }

                        if (retryData?.desativaFeedbackPorMensagem?.pollingAgendamento === true) {
                            return await pollAgendamento();
                        }

                        return retryData;
                    } catch (retryError) {
                        console.error('Erro na chamada de pollingAgendamento no sendMessage:', retryError);
                        return data;
                    }
                };

                return pollAgendamento();
            }

            // Verificar pollingBindExams e disparar nova chamada automática ao sendMessage
            if (data?.pollingBindExams === true) {
                // Intervalo configurável por env, padrão 5000ms (5 segundos)
                const envInterval = parseInt(process.env.REACT_APP_POLLING_BIND_EXAMS_INTERVAL_MS || '0', 10);
                const delayMs = Number.isFinite(envInterval) && envInterval > 0 ? envInterval : 5000;

                console.log(`⏱️ pollingBindExams recebido, agendando nova chamada de sendMessage em ${delayMs}ms...`);

                setTimeout(() => {
                    try {
                        const pollMessage = 'checa-vinculo-exames';
                        console.log('🔁 Disparando sendMessage para checar vínculo de exames (pollingBindExames)...');
                        sendMessage(
                            setMessages,
                            client_id,
                            pollMessage,
                            messages,
                            marca,
                            origem,
                            teste,
                            localizacao,
                            latitude,
                            longitude,
                            pollMessage,
                            setTeste,
                            getLocation,
                            operational_system,
                            version_app,
                            null, // examesSelecionados já foram enviados na chamada anterior
                            additionalContext,
                            requestImage,
                            setRequestId,
                            insuranceSelected
                        );
                    } catch (err) {
                        console.error('❌ Erro ao disparar pollingBindExams via sendMessage:', err);
                    }
                }, delayMs);
            }

            if (
                data?.perguntasApoio === "N/A" &&
                (!data?.options || data?.options.length === 0) &&
                (
                    (data?.bloqueiaInput !== true && data?.respostaExtensa !== true) ||
                    (
                        typeof data?.response === 'string' &&
                        (
                            data?.response.includes('Exames vinculados com sucesso!') ||
                            data?.response.includes('Procedimentos vinculados com sucesso!')
                        )
                    )
                )
            ) {
                console.log('perguntasApoio retornou N/A e options está vazio, fazendo nova chamada silenciosa...');

                const retryBodyContext = {
                    context: {
                        ...bodyContext.context,
                    }
                };

                return authenticatedFetch(`${getApiBaseUrl()}/conversation`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        ...getAuthHeader()
                    },
                    body: JSON.stringify({ 
                        message: "", 
                        client_id: resolvedClientId, 
                        conversationId: resolvedConversationId,
                        conversation_id: resolvedConversationId,
                        marca, 
                        origem, 
                        ...retryBodyContext, 
                        start: false,
                        organizationId: getOrganizationId()
                    })
                }).then(retryData => {
                    console.log('Resposta da API sendMessage (retry):', JSON.stringify(retryData));

                    // Atualizar requestId se necessário
                    if (retryData?.requisicaoAgendamento && retryData?.requisicaoAgendamento !== '') {
                        setRequestId(retryData?.requisicaoAgendamento)
                    }

                    if (retryData?.idFluxoAgendamento && String(retryData.idFluxoAgendamento).trim() !== '') {
                        const newIdFluxo = String(retryData.idFluxoAgendamento).trim();
                        try {
                            if (typeof window !== 'undefined') {
                                window.currentIdFluxoAgendamento = newIdFluxo;
                            }
                        } catch (e) {
                            // ignore
                        }
                    }

                    if (retryData?.person && typeof retryData.person === 'object') {
                        try {
                            if (typeof window !== 'undefined') {
                                window.currentPerson = retryData.person;
                                const maybeCustomer = retryData.person?.customer || retryData.person?.custumer || null;
                                if (maybeCustomer && typeof maybeCustomer === 'object') {
                                    window.currentCustomer = maybeCustomer;
                                }
                            }
                        } catch (e) {
                            // ignore
                        }
                    }
                    if (retryData?.cliente && typeof retryData.cliente === 'object') {
                        try {
                            if (typeof window !== 'undefined') {
                                window.currentCustomer = retryData.cliente;
                            }
                        } catch (e) {
                            // ignore
                        }
                    }

                    if (retryData?.unidadeSelecionada && String(retryData.unidadeSelecionada).trim() !== '') {
                        const unidade = String(retryData.unidadeSelecionada).trim();
                        try {
                            if (typeof window !== 'undefined') {
                                window.currentUnidadeSelecionada = unidade;
                            }
                        } catch (e) {
                            // ignore
                        }
                    }

                    // Capturar sexo do cliente do retryData
                    try {
                        const rawSexo = retryData?.clienteSexo || retryData?.context?.slots?.clienteSexo;
                        if (typeof window !== 'undefined' && typeof rawSexo === 'string') {
                            const sexo = rawSexo.trim().toUpperCase();
                            if (sexo === 'M' || sexo === 'F') {
                                window.currentClienteSexo = sexo;
                            }
                        }
                    } catch (e) {
                        // ignore
                    }

                    // Usar os dados da segunda chamada
                    return retryData;
                }).catch(retryError => {
                    console.error('Erro na chamada de retry:', retryError);
                    // Em caso de erro no retry, usar os dados originais
                    return data;
                });
            }

            // Se não for N/A, continuar com os dados normais
            return data;
        })
        .then(data => {
            // The provider start only activates polling; preserve typing until the callback arrives.
            if (data?.externalProviderFlow === true) {
                return data;
            }

            // Se for ocrApenasPedidoMedico, não processar mensagens normais
            if (data?.ocrApenasPedidoMedico && data?.ocrApenasPedidoMedico === true) {
                console.log('🔍 Pulando processamento de mensagens para OCR Pedido Médico (sendMessage)');
                return; // Sai do fluxo sem processar mensagens
            }

            if (data?.getLoc && data?.getLoc !== false) {
                if (((localizacao !== null) && (latitude !== '' && longitude !== '') || ((localizacao == null) && (latitude !== '' && longitude !== '')))) {
                    getLocation()
                    if (messages[messages.length - 1].origin === 'bot') {
                        setMessages(messages => messages.slice(0, -1));
                    }
                } else {
                    getLocation()
                    if (messages[messages.length - 1].origin === 'bot') {
                        setMessages(messages => messages.slice(0, -1));

                    }
                }
            }

            if (data?.getLoc && data?.getLoc === true) {

                getLocation()

            } else if ((data?.ocrCarteirinhaNegativo && data?.ocrCarteirinhaNegativo === true) && (data?.componenteConvenio === true || data?.desativaFeedbackPorMensagem?.componenteConvenio === true)) {
                // OCR carteirinha com resultado negativo: abrir componente de convênio manualmente
                const preselectedId = (typeof window !== 'undefined' && window.currentInsuranceSelected && window.currentInsuranceSelected.insurance && window.currentInsuranceSelected.insurance.id) ? String(window.currentInsuranceSelected.insurance.id) : '';
                setMessages(messages => messages.slice(0, -1));
                setMessages(messages => [...messages, {
                    origin: 'bot',
                    response: data?.response || 'Por favor, informe os dados do seu convênio.',
                    options: [],
                    buttonLink: data.buttonLink === true,
                    buttonLinkText: data.buttonLinkText || '',
                    componenteConvenio: true,
                    preselectedInsuranceId: preselectedId,
                    forceManualConvenioFlow: true,
                    examesSelecionados: [...messages].reverse().find(m => m?.examesSelecionados)?.examesSelecionados || null,
                    desativaFeedbackPorMensagem: { desativaFeedbackPorMensagem: true, componenteConvenio: true }
                }]);

            } else if (data?.ocrCarteirinha && data?.ocrCarteirinha === true) {
                // Verificar se OCR de carteirinha está habilitado via variável de ambiente
                const ocrCarteirinhaEnabled = process.env.REACT_APP_OCR_CARTEIRINHA_ENABLED !== 'false';

                if (ocrCarteirinhaEnabled) {
                    requestImage(data?.requisicaoAgendamento, 'ocr-carteirinha', data?.idPessoaFisica, data?.ocrProvider === 'firstlayer' ? 'firstlayer' : 'fleury')
                    setRequestId(data?.requisicaoAgendamento)
                } else {
                    const preselectedId = (typeof window !== 'undefined' && window.currentInsuranceSelected && window.currentInsuranceSelected.insurance && window.currentInsuranceSelected.insurance.id) ? String(window.currentInsuranceSelected.insurance.id) : '';
                    // OCR desabilitado: abrir componente de convênio manualmente
                    setMessages(messages => messages.slice(0, -1));
                    setMessages(messages => [...messages, {
                        origin: 'bot',
                        response: data?.response || 'Por favor, selecione seu convênio, empresa e plano.',
                        options: [],
                        buttonLink: data.buttonLink === true,
                        buttonLinkText: data.buttonLinkText || '',
                        componenteConvenio: true,
                        preselectedInsuranceId: preselectedId,
                        forceManualConvenioFlow: false,
                        desativaFeedbackPorMensagem: { desativaFeedbackPorMensagem: true, componenteConvenio: true }
                    }]);
                }

            } else if (data?.buscaExamesOcr && data?.buscaExamesOcr === true && !(data?.ocrApenasPedidoMedico && data?.ocrApenasPedidoMedico === true)) {

                requestImage(data?.requisicaoAgendamento, 'normal', data?.idPessoaFisica)
                setRequestId(data?.requisicaoAgendamento)

            } else {
                setMessages(messages => messages.slice(0, -1));

                if (data.response && data.response.includes('$#$')) {
                    const [firstMessage, secondMessage] = data.response.split('$#$');

                    setMessages(messages => [...messages,
                    {
                        origin: 'bot',
                        response: firstMessage.trim(),
                        options: [],
                        request: data.request || '',
                        carrossel: null,
                        bloqueiaInput: false,
                        bloqueiaInputLetras: data.bloqueiaInputLetras || data.context?.slots?.bloqueiaInputLetras || data.slots?.bloqueiaInputLetras || false,
                        inputReclamacao: false,
                        inputElogio: false,
                        inputSugestao: false,
                        isFirstMessage: true,
                        autoservice: data.autosservico || "",
                        buscaExamesTextual: data.buscaExamesTextual || false,
                        buscaExamesMovel: data.buscaExamesMovel || false,
                        agendamentoConvenio: data.agendamentoConvenio || false,
                        allowVaccines: data.allowVaccines || false,
                        buttonLink: data.buttonLink || false,
                        buttonLinkText: data.buttonLinkText || '',
                        componenteRemocaoProcedimentos: data.componenteRemocaoProcedimentos || false,
                        examesSelecionados: data.examesSelecionados || [...messages].reverse().find(m => m?.examesSelecionados)?.examesSelecionados || null,
                        desativaFeedbackPorMensagem: (typeof data?.desativaFeedbackPorMensagem === 'object' ? data.desativaFeedbackPorMensagem : ((data?.desativaFeedbackPorMensagem === true || data?.desativaFeedbackPorMensagem?.desativaFeedbackPorMensagem === true) || (Array.isArray(data?.examesAutorizacao) && data.examesAutorizacao.length > 0))),
                        examesAutorizacao: Array.isArray(data?.examesAutorizacao) ? data.examesAutorizacao : [],
                        perguntasApoio: null, // Primeira mensagem não terá perguntas
                        agendamentoAdicionaProcedimentos: data.agendamentoAdicionaProcedimentos || false,
                        componenteConvenio: data.componenteConvenio || false,
                        iniciarPagamento: data.iniciarPagamento || false,
                        valorPagamento: data.valorPagamento ?? null,
                        detalhesOcultos: Array.isArray(data.detalhesOcultos) ? data.detalhesOcultos : [],
                        detalhesOcultosTag: data.detalhesOcultosTag || '',
                        unidadeSelecionadaNome: data.unidadeSelecionadaNome || null
                    },
                    {
                        origin: 'bot',
                        response: secondMessage.trim(),
                        options: data.options || [],
                        request: data.request || '',
                        carrossel: data.carrossel || null,
                        bloqueiaInput: data.bloqueiaInput || false,
                        bloqueiaInputLetras: data.bloqueiaInputLetras || data.context?.slots?.bloqueiaInputLetras || data.slots?.bloqueiaInputLetras || false,
                        inputReclamacao: data.inputReclamacao || false,
                        inputElogio: data.inputElogio || false,
                        inputSugestao: data.inputSugestao || false,
                        isFirstMessage: false,
                        csat: data.csat || false,
                        autoservice: data.autosservico || "",
                        buscaExamesTextual: data.buscaExamesTextual || false,
                        buscaExamesMovel: data.buscaExamesMovel || false,
                        agendamentoConvenio: data.agendamentoConvenio || false,
                        allowVaccines: data.allowVaccines || false,
                        buttonLink: data.buttonLink || false,
                        buttonLinkText: data.buttonLinkText || '',
                        componenteRemocaoProcedimentos: data.componenteRemocaoProcedimentos || false,
                        examesSelecionados: data.examesSelecionados || [...messages].reverse().find(m => m?.examesSelecionados)?.examesSelecionados || null,
                        desativaFeedbackPorMensagem: (typeof data?.desativaFeedbackPorMensagem === 'object' ? data.desativaFeedbackPorMensagem : ((data?.desativaFeedbackPorMensagem === true || data?.desativaFeedbackPorMensagem?.desativaFeedbackPorMensagem === true) || (Array.isArray(data?.examesAutorizacao) && data.examesAutorizacao.length > 0))),
                        examesAutorizacao: Array.isArray(data?.examesAutorizacao) ? data.examesAutorizacao : [],
                        perguntasApoio: (data.perguntasApoio && data.perguntasApoio !== "N/A") ? data.perguntasApoio : null, // Segunda mensagem pode ter perguntas
                        agendamentoAdicionaProcedimentos: data.agendamentoAdicionaProcedimentos || false,
                        respostaExtensa: data.respostaExtensa || false,
                        componenteConvenio: data.componenteConvenio || false,
                        iniciarPagamento: data.iniciarPagamento || false,
                        valorPagamento: data.valorPagamento ?? null,
                        detalhesOcultos: Array.isArray(data.detalhesOcultos) ? data.detalhesOcultos : [],
                        detalhesOcultosTag: data.detalhesOcultosTag || '',
                        unidadeSelecionadaNome: data.unidadeSelecionadaNome || null
                    }
                    ]);
                } else {
                    setMessages(messages => {
                        const newMessage = {
                            origin: 'bot',
                            response: data.response,
                            options: data.options || [],
                            request: data.request || '',
                            carrossel: data.carrossel || null,
                            bloqueiaInput: data.bloqueiaInput || false,
                            bloqueiaInputLetras: data.bloqueiaInputLetras || data.context?.slots?.bloqueiaInputLetras || data.slots?.bloqueiaInputLetras || false,
                            inputReclamacao: data.inputReclamacao || false,
                            inputElogio: data.inputElogio || false,
                            inputSugestao: data.inputSugestao || false,
                            csat: data.csat || false,
                            autoservice: data.autosservico || "",
                            buscaExamesTextual: data.buscaExamesTextual || false,
                            buscaExamesMovel: data.buscaExamesMovel || false,
                            agendamentoConvenio: data.agendamentoConvenio || false,
                            allowVaccines: data.allowVaccines || false,
                            buttonLink: data.buttonLink || false,
                            buttonLinkText: data.buttonLinkText || '',
                            componenteRemocaoProcedimentos: data.componenteRemocaoProcedimentos || false,
                            examesSelecionados: data.examesSelecionados || [...messages].reverse().find(m => m?.examesSelecionados)?.examesSelecionados || null,
                            desativaFeedbackPorMensagem: (typeof data?.desativaFeedbackPorMensagem === 'object' ? data.desativaFeedbackPorMensagem : ((data?.desativaFeedbackPorMensagem === true || data?.desativaFeedbackPorMensagem?.desativaFeedbackPorMensagem === true) || (Array.isArray(data?.examesAutorizacao) && data.examesAutorizacao.length > 0))),
                            examesAutorizacao: Array.isArray(data?.examesAutorizacao) ? data.examesAutorizacao : [],
                            perguntasApoio: (data.perguntasApoio && data.perguntasApoio !== "N/A") ? data.perguntasApoio : null, // Adicionar perguntas de apoio se existirem
                            agendamentoAdicionaProcedimentos: data.agendamentoAdicionaProcedimentos || false,
                            respostaExtensa: data.respostaExtensa || false,
                            componenteConvenio: data.componenteConvenio || false,
                            iniciarPagamento: data.iniciarPagamento || false,
                            valorPagamento: data.valorPagamento ?? null,
                            detalhesOcultos: Array.isArray(data.detalhesOcultos) ? data.detalhesOcultos : [],
                            detalhesOcultosTag: data.detalhesOcultosTag || '',
                            unidadeSelecionadaNome: data.unidadeSelecionadaNome || null,
                            pollingBindExams: data.pollingBindExams || false
                        };

                        if (messages.length > 0 && messages[messages.length - 1].pollingBindExams && newMessage.pollingBindExams) {
                            return messages; // Evita bolhas de carregamento duplicadas consecutivas
                        }

                        return [...messages, newMessage];
                    });
                }
            }
        })
        .catch(error => {
            if (additionalContext?.attachment) {
                // Let the upload caller display a retry action; never route this file to OCR.
                return false;
            }
            console.error('Erro na comunicação com a API:', error);
            setMessages(messages => messages.slice(0, -1)); // Remove a mensagem de "Digitando..."
            setMessages(messages => [...messages, {
                origin: 'bot',
                response: 'Desculpe, ocorreu um erro na comunicação com o servidor. Por favor, tente novamente mais tarde.',
                options: [],
                request: '',
                carrossel: null,
                bloqueiaInput: false,
                desativaFeedbackPorMensagem: true, // Desativar feedback para mensagens de erro
                agendamentoAdicionaProcedimentos: false,
                componenteConvenio: false
            }]);
        });
};

export function sendFeedback(estado, input, response, uuid, client_id) {
    // Transforme o array de mensagens em um objeto JSON
    // Faça a chamada de API usando o método fetch
    fetch(`${getApiBaseUrl()}/feedback`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getAuthHeader()
        },
        body: JSON.stringify({ feedback: estado, input: input, response: response, uuid: uuid, client_id: client_id })
    })
        .then(response => {
            if (!response.ok) {
                throw new Error(`Erro na requisição: ${response.status} ${response.statusText}`);
            }
            return response.text().then(text => {
                if (!text || text.trim() === '') {
                    return {};
                }
                try {
                    return JSON.parse(text);
                } catch (error) {
                    console.error('Erro ao processar resposta JSON:', error);
                    console.error('Texto da resposta:', text);
                    return {};
                }
            });
        })
        .then(data => {
            // Faça algo com a resposta da API
            console.log('Feedback enviado com sucesso');
        })
        .catch(error => {
            console.error('Erro ao enviar feedback:', error);
        });
};

/**
 * Converte uma URI para uma URL autenticada de login (para usuários da área logada).
 * Chama a Insurance API com action "ConvertUriToLoginAction" e retorna a schedulingRedirectUrl.
 * @param {string} client_id - ID do cliente (criptografado da URL)
 * @param {number} [brandId=17] - ID da marca
 * @param {number} [officeId=5] - ID do escritório
 * @returns {Promise<string|null>} URL autenticada ou null em caso de erro
 */
export async function convertUriToLoginAction(client_id, brandId = 17, officeId = 5) {
    try {
        const insuranceApiUrl = process.env.REACT_APP_INSURANCE_API_URL;
        const insuranceApiKey = process.env.REACT_APP_INSURANCE_API_KEY;
        // Secure flow: the BFF holds the Insurance API URL and key
        const useBff = Boolean(process.env.REACT_APP_BFF_API_URL) || !insuranceApiKey;

        if (!useBff && !insuranceApiUrl) {
            console.warn('REACT_APP_INSURANCE_API_URL não configurada. Não é possível converter URI para login.');
            return null;
        }

        const body = {
            context: {
                action: 'ConvertUriToLoginAction',
                person: {
                    idCliente: String(client_id)
                },
                brandId,
                officeId
            }
        };

        console.log('🔐 Chamando ConvertUriToLoginAction para client_id:', client_id);

        if (useBff) {
            const bffData = await bffExecuteInsuranceAction(body.context, '');
            const bffRedirectUrl = bffData?.context?.schedulingRedirectUrl;
            if (!bffRedirectUrl) {
                console.warn('⚠️ schedulingRedirectUrl não encontrada na resposta do BFF:', bffData);
                return null;
            }
            return bffRedirectUrl;
        }

        const response = await fetch(insuranceApiUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(insuranceApiKey ? { 'x-api-key': insuranceApiKey } : {})
            },
            body: JSON.stringify(body)
        });

        if (!response.ok) {
            console.error('❌ Erro ao chamar ConvertUriToLoginAction:', response.status, response.statusText);
            return null;
        }

        const data = await response.json();
        console.log('✅ ConvertUriToLoginAction resposta:', data);

        const redirectUrl = data?.context?.schedulingRedirectUrl;
        if (!redirectUrl) {
            console.warn('⚠️ schedulingRedirectUrl não encontrada na resposta:', data);
            return null;
        }

        return redirectUrl;
    } catch (error) {
        console.error('❌ Erro ao converter URI para login:', error);
        return null;
    }
}

export function sendClickLink(client_id, link, conversation_id, origin) {
    // Fluxo seguro via BFF sem expor credenciais Sensedia
    if (process.env.REACT_APP_BFF_API_URL || !process.env.REACT_APP_SENSEDIA_SECRETID) {
        return bffRegisterLink(client_id, link, conversation_id, origin)
            .then(() => {
                console.log('Clique em link registrado com sucesso via BFF');
            })
            .catch(error => {
                console.error('Erro ao registrar clique em link via BFF:', error);
            });
    }

    fetch(`${getApiBaseUrl()}/links/register`, {
        method: 'POST',
        headers: {
            'client_id': process.env.REACT_APP_SENSEDIA_CLIENTID,
            "secret_id": process.env.REACT_APP_SENSEDIA_SECRETID,
            'Content-Type': 'application/json',
            ...getAuthHeader()
        },
        body: JSON.stringify({ client_id: client_id, link: link, conversationId: conversation_id, origin: origin })
    })
        .then(response => {
            if (!response.ok) {
                throw new Error(`Erro na requisição: ${response.status} ${response.statusText}`);
            }
            return response.text().then(text => {
                if (!text || text.trim() === '') {
                    return {};
                }
                try {
                    return JSON.parse(text);
                } catch (error) {
                    console.error('Erro ao processar resposta JSON:', error);
                    console.error('Texto da resposta:', text);
                    return {};
                }
            });
        })
        .then(data => {
            // Faça algo com a resposta da API
            console.log('Clique em link registrado com sucesso');
        })
        .catch(error => {
            console.error('Erro ao registrar clique em link:', error);
        });
};

/**
 * Função de depuração para validar dados PDF
 * @param {string} base64Data - Dados base64 a serem validados
 * @returns {boolean} - True se os dados parecem ser um PDF válido
 */
function validatePdfData(base64Data) {
    try {
        // Verificar se o tamanho é sensato (alguns KBs no mínimo)
        if (base64Data.length < 100) {
            console.error("Dados base64 muito pequenos para ser um PDF válido");
            return false;
        }

        // Verificar o início dos dados binários (PDFs começam com %PDF-)
        const byteCharacters = atob(base64Data.substring(0, 100));

        // Converter os primeiros caracteres em uma string legível
        let pdfHeader = "";
        for (let i = 0; i < Math.min(10, byteCharacters.length); i++) {
            pdfHeader += byteCharacters[i];
        }

        console.log("Início do PDF:", pdfHeader);

        // Verificar header PDF
        if (!pdfHeader.startsWith("%PDF-")) {
            console.error("Os dados não começam com a assinatura PDF (%PDF-)");
            console.log("Primeiros 20 caracteres do base64:", base64Data.substring(0, 20));
            return false;
        }

        return true;
    } catch (error) {
        console.error("Erro ao validar dados PDF:", error);
        return false;
    }
}

/**
 * Detecta se o site está rodando dentro de um WebView de app móvel
 * @returns {Object} Informações sobre o ambiente
 */
function detectEnvironment() {
    const userAgent = navigator.userAgent.toLowerCase();
    const isAndroid = userAgent.indexOf('android') > -1;
    const isIOS = /iphone|ipad|ipod/.test(userAgent);
    const isInIframe = window !== window.top;
    const hasAndroidInterface = typeof window.Android !== 'undefined';
    const hasIOSInterface = window.webkit && window.webkit.messageHandlers;

    // Log detalhado para diagnóstico
    console.log("Detecção de ambiente:", {
        userAgent,
        isAndroid,
        isIOS,
        isInIframe,
        hasAndroidInterface,
        hasIOSInterface
    });

    return {
        isAndroid,
        isIOS,
        isInIframe,
        hasAndroidInterface,
        hasIOSInterface,
        isWebView: isInIframe || hasAndroidInterface || hasIOSInterface
    };
}

/**
 * Método alternativo para download em WebView Android
 * @param {string} base64Data - Dados PDF em base64
 * @param {string} fileName - Nome do arquivo
 * @returns {boolean} - True se o método foi aplicado
 */
function androidAlternativeDownload(base64Data, fileName) {
    try {
        console.log("Tentando método alternativo para Android");

        // Método 1: Usando a API de download do Android (disponível em WebViews mais recentes)
        if (typeof window.Android !== 'undefined') {
            console.log("Tentando usar interface Android global");
            if (typeof window.Android.downloadPdfFromBase64 === 'function') {
                window.Android.downloadPdfFromBase64(base64Data, fileName);
                return true;
            }
        }

        // Método 2: Tenta forçar um download via Content-Disposition
        const blob = base64ToBlob(base64Data, 'application/pdf');
        const url = URL.createObjectURL(blob);

        // Cria um iframe escondido para tentar o download
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        document.body.appendChild(iframe);

        try {
            // Verifica se temos acesso ao contentWindow antes de tentar acessá-lo
            // Evita erro de cross-origin
            try {
                if (iframe.contentWindow) {
                    iframe.contentWindow.name = 'pdfDownloader';
                }
            } catch (crossOriginError) {
                console.log("Não foi possível acessar contentWindow do iframe (cross-origin), continuando sem nome de janela");
            }

            // Abre o URL no iframe, o que pode acionar o download no Android
            iframe.src = url;

            // Força a navegação usando uma abordagem alternativa
            setTimeout(() => {
                const a = document.createElement('a');
                a.href = url;
                a.download = fileName;
                a.target = 'pdfDownloader';
                a.rel = 'noopener noreferrer';
                a.click();

                // Limpeza
                setTimeout(() => {
                    document.body.removeChild(iframe);
                    URL.revokeObjectURL(url);
                }, 1000);
            }, 100);

            return true;
        } catch (iframeError) {
            console.error("Erro no método iframe:", iframeError);
            document.body.removeChild(iframe);
            return false;
        }
    } catch (error) {
        console.error("Erro no download alternativo para Android:", error);
        return false;
    }
}

/**
 * Converte base64 para Blob
 * @param {string} base64 - String base64
 * @param {string} mimeType - Tipo MIME do conteúdo
 * @returns {Blob} - Objeto Blob
 */
function base64ToBlob(base64, mimeType) {
    try {
        // Decodifica o base64
        const byteCharacters = atob(base64);
        const byteArrays = [];

        // Processa em blocos para evitar problemas de memória
        for (let offset = 0; offset < byteCharacters.length; offset += 512) {
            const slice = byteCharacters.slice(offset, offset + 512);

            const byteNumbers = new Array(slice.length);
            for (let i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
            }

            const byteArray = new Uint8Array(byteNumbers);
            byteArrays.push(byteArray);
        }

        return new Blob(byteArrays, { type: mimeType });
    } catch (error) {
        console.error("Erro na conversão base64 para Blob:", error);
        throw error;
    }
}

/**
 * Exibe uma mensagem para o usuário sobre problemas com download
 */
function showDownloadHelp() {
    const helpDiv = document.createElement('div');
    helpDiv.style.position = 'fixed';
    helpDiv.style.bottom = '20px';
    helpDiv.style.left = '20px';
    helpDiv.style.right = '20px';
    helpDiv.style.backgroundColor = '#f8d7da';
    helpDiv.style.color = '#721c24';
    helpDiv.style.padding = '10px';
    helpDiv.style.borderRadius = '5px';
    helpDiv.style.boxShadow = '0 2px 5px rgba(0,0,0,0.2)';
    helpDiv.style.zIndex = '999998';
    helpDiv.style.textAlign = 'center';
    helpDiv.style.fontSize = '14px';

    helpDiv.innerHTML = `
        <p>Não foi possível baixar o PDF automaticamente.</p>
        <p>Por favor, atualize o aplicativo para a versão mais recente ou entre em contato com o suporte.</p>
        <button style="background: #dc3545; color: white; border: none; padding: 5px 10px; border-radius: 3px; margin-top: 5px;">Fechar</button>
    `;

    helpDiv.querySelector('button').onclick = () => {
        document.body.removeChild(helpDiv);
    };

    document.body.appendChild(helpDiv);
}

/**
 * Envia PDF para o aplicativo nativo para download ou visualização
 * @param {string} base64Data - Dados PDF em base64
 * @param {string} fileName - Nome do arquivo
 * @returns {boolean} True se o envio foi bem-sucedido
 */
function sendPdfToNativeApp(base64Data, fileName) {
    try {
        console.log("Tentando enviar PDF para app nativo");
        const env = detectEnvironment();

        // Proteção contra dados muito grandes ou inválidos
        if (!base64Data || base64Data.length <= 0) {
            console.error("Dados base64 vazios ou inválidos");
            return false;
        }

        // Verifica a interface Android específica (WebAppInterface)
        if (env.hasAndroidInterface) {
            console.log("Interface Android detectada");

            try {
                // Exibir visualizador interno em vez de tentar download nativo
                // Isso é mais seguro e evita crashes quando a implementação nativa tem problemas
                console.log("Exibindo visualizador interno em vez de usar API nativa");
                const pdfViewer = createPdfViewer(base64Data);
                document.body.appendChild(pdfViewer);
                return true;

                /* Código comentado para evitar crashes - implementar depois que o desenvolvedor
                   corrigir a interface nativa:
                   
                // Interface para Android (window.Android deve ser definido pelo app nativo)
                if (window.Android && typeof window.Android.downloadPdfFromBase64 === 'function') {
                    console.log("Função downloadPdfFromBase64 encontrada, chamando...");
                    window.Android.downloadPdfFromBase64(base64Data, fileName);
                    console.log("Chamada de função Android concluída");
                    return true;
                }
                */
            } catch (androidError) {
                console.error("Erro no ambiente Android:", androidError);
                // Continuar para alternativas
            }
        }

        // Interface para iOS (usando WKWebView message handlers)
        if (env.hasIOSInterface && window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.downloadPdf) {
            console.log("Enviando PDF para app iOS");
            try {
                window.webkit.messageHandlers.downloadPdf.postMessage({
                    base64Data: base64Data,
                    fileName: fileName
                });
                return true;
            } catch (iosError) {
                console.error("Erro ao enviar para iOS:", iosError);
                // Continuar para alternativas
            }
        }

        // Método alternativo: tentar via iframe
        try {
            const blob = base64ToBlob(base64Data, 'application/pdf');
            const blobUrl = URL.createObjectURL(blob);

            // Abrir em nova janela/guia pode funcionar em alguns WebViews
            window.open(blobUrl, '_blank');

            // Mostrar mensagem de ajuda para o usuário
            if (env.isAndroid || env.isIOS) {
                console.log("Ambiente móvel detectado, mostrando mensagem de ajuda");
                showDownloadHelp();
            }

            // Como backup, mostrar visualizador embutido
            setTimeout(() => {
                const pdfViewer = createPdfViewer(base64Data);
                document.body.appendChild(pdfViewer);
            }, 1000);

            return true;
        } catch (blobError) {
            console.error("Erro ao criar blob:", blobError);
        }

        // Último recurso: visualizador embutido
        console.log("Usando visualizador embutido como última opção");
        const pdfViewer = createPdfViewer(base64Data);
        document.body.appendChild(pdfViewer);
        return true;
    } catch (error) {
        console.error("Erro ao enviar PDF para app nativo:", error);

        // Tenta exibir o PDF como último recurso
        try {
            const pdfViewer = createPdfViewer(base64Data);
            document.body.appendChild(pdfViewer);
            return true;
        } catch (viewerError) {
            console.error("Erro ao criar visualizador de PDF:", viewerError);
            showFallbackMessage();
            return false;
        }
    }
}

/**
 * Mostra uma mensagem de fallback quando tudo falha
 */
function showFallbackMessage() {
    const messageDiv = document.createElement('div');
    messageDiv.style.position = 'fixed';
    messageDiv.style.top = '50%';
    messageDiv.style.left = '50%';
    messageDiv.style.transform = 'translate(-50%, -50%)';
    messageDiv.style.backgroundColor = 'white';
    messageDiv.style.padding = '20px';
    messageDiv.style.borderRadius = '5px';
    messageDiv.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
    messageDiv.style.zIndex = '999999';
    messageDiv.style.maxWidth = '80%';
    messageDiv.style.textAlign = 'center';

    messageDiv.innerHTML = `
        <h3 style="color: #721c24; margin-top: 0;">Não foi possível exibir o PDF</h3>
        <p>Houve um problema ao tentar exibir ou baixar o documento.</p>
        <p>Por favor, tente novamente mais tarde ou entre em contato com o suporte.</p>
        <button style="background: #007bff; color: white; border: none; padding: 8px 16px; border-radius: 4px; margin-top: 10px; cursor: pointer;">OK</button>
    `;

    messageDiv.querySelector('button').onclick = () => {
        document.body.removeChild(messageDiv);
    };

    document.body.appendChild(messageDiv);
}

/**
 * Cria um iframe para exibir o PDF diretamente na página
 * @param {string} base64Data - Dados PDF em base64
 * @returns {HTMLElement} Elemento div contendo o iframe com o PDF
 */
function createPdfViewer(base64Data) {
    console.log("Criando visualizador de PDF embutido");

    try {
        // Criar container com estilo de modal
        const container = document.createElement('div');
        container.style.position = 'fixed';
        container.style.top = '0';
        container.style.left = '0';
        container.style.width = '100%';
        container.style.height = '100%';
        container.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
        container.style.zIndex = '999999';
        container.style.display = 'flex';
        container.style.flexDirection = 'column';
        container.style.alignItems = 'center';
        container.style.justifyContent = 'center';

        // Criar cabeçalho com título e botão de fechar
        const header = document.createElement('div');
        header.style.width = '90%';
        header.style.display = 'flex';
        header.style.justifyContent = 'space-between';
        header.style.alignItems = 'center';
        header.style.padding = '10px';
        header.style.backgroundColor = '#343a40';
        header.style.borderTopLeftRadius = '5px';
        header.style.borderTopRightRadius = '5px';

        const title = document.createElement('span');
        title.innerText = 'Visualizador de PDF';
        title.style.color = 'white';
        title.style.fontWeight = 'bold';

        const closeButton = document.createElement('button');
        closeButton.innerText = 'X';
        closeButton.style.padding = '5px 10px';
        closeButton.style.backgroundColor = '#dc3545';
        closeButton.style.color = 'white';
        closeButton.style.border = 'none';
        closeButton.style.borderRadius = '3px';
        closeButton.style.cursor = 'pointer';
        closeButton.style.fontSize = '14px';

        closeButton.onclick = () => {
            try {
                document.body.removeChild(container);
            } catch (e) {
                console.error("Erro ao remover visualizador:", e);
            }
        };

        header.appendChild(title);
        header.appendChild(closeButton);

        // Container para o conteúdo do PDF
        const viewerContainer = document.createElement('div');
        viewerContainer.style.width = '90%';
        viewerContainer.style.height = '80%';
        viewerContainer.style.backgroundColor = 'white';
        viewerContainer.style.overflow = 'hidden';

        // Criar conteúdo usando img em vez de iframe (melhor compatibilidade)
        const pdfContent = document.createElement('div');
        pdfContent.style.width = '100%';
        pdfContent.style.height = '100%';
        pdfContent.style.overflow = 'auto';
        pdfContent.style.display = 'flex';
        pdfContent.style.justifyContent = 'center';
        pdfContent.style.alignItems = 'center';
        pdfContent.innerHTML = `
            <div style="padding: 20px; text-align: center;">
                <p>O PDF está sendo processado...</p>
                <p>Caso não carregue automaticamente, use o botão abaixo para baixar.</p>
            </div>
        `;

        viewerContainer.appendChild(pdfContent);

        // Tentar adicionar o PDF usando iframe apenas depois que o container estiver pronto
        setTimeout(() => {
            try {
                // Tentar usar embed (mais compatível que iframe)
                const embed = document.createElement('embed');
                embed.src = `data:application/pdf;base64,${base64Data}`;
                embed.type = 'application/pdf';
                embed.style.width = '100%';
                embed.style.height = '100%';

                // Limpar conteúdo atual e adicionar o PDF
                pdfContent.innerHTML = '';
                pdfContent.appendChild(embed);
            } catch (embedError) {
                console.error("Erro ao criar embed para PDF:", embedError);

                // Fallback para mostrar mensagem
                pdfContent.innerHTML = `
                    <div style="padding: 20px; text-align: center; background-color: #f8d7da; color: #721c24;">
                        <p>Não foi possível exibir o PDF neste dispositivo.</p>
                        <p>Use o botão abaixo para baixar e abrir em um visualizador externo.</p>
                    </div>
                `;
            }
        }, 100);

        // Adicionar botão de download para salvar cópia local
        const downloadButton = document.createElement('button');
        downloadButton.innerText = 'Baixar PDF';
        downloadButton.style.padding = '10px 20px';
        downloadButton.style.margin = '10px';
        downloadButton.style.backgroundColor = '#28a745';
        downloadButton.style.color = 'white';
        downloadButton.style.border = 'none';
        downloadButton.style.borderRadius = '5px';
        downloadButton.style.cursor = 'pointer';
        downloadButton.style.fontSize = '16px';

        downloadButton.onclick = () => {
            try {
                // Abrir diretamente no navegador
                const newWindow = window.open("", "_blank");
                if (newWindow) {
                    // Verifica se temos permissão para acessar o documento da nova janela
                    try {
                        newWindow.document.write(`
                        <html>
                            <head>
                                <title>Visualizador de PDF</title>
                            </head>
                            <body style="margin: 0; padding: 0;">
                                <embed 
                                    src="data:application/pdf;base64,${base64Data}" 
                                    type="application/pdf" 
                                    width="100%" 
                                    height="100%"
                                    style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;"
                                />
                            </body>
                        </html>
                    `);
                    } catch (crossOriginError) {
                        console.log("Não foi possível acessar documento da nova janela (cross-origin)");
                        // Tenta usar blob URL como alternativa
                        const blob = base64ToBlob(base64Data, 'application/pdf');
                        const blobUrl = URL.createObjectURL(blob);
                        newWindow.location.href = blobUrl;
                        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
                    }
                } else {
                    // Fallback para criar um blob e forçar o download
                    try {
                        const blob = base64ToBlob(base64Data, 'application/pdf');
                        const blobUrl = URL.createObjectURL(blob);

                        const a = document.createElement('a');
                        a.href = blobUrl;
                        a.download = `documento-${new Date().toISOString().slice(0, 10)}.pdf`;
                        a.style.display = 'none';

                        document.body.appendChild(a);
                        a.click();

                        setTimeout(() => {
                            document.body.removeChild(a);
                            URL.revokeObjectURL(blobUrl);
                        }, 100);
                    } catch (blobError) {
                        console.error("Erro ao criar blob:", blobError);
                        alert("Não foi possível baixar o arquivo. Por favor, tente novamente mais tarde.");
                    }
                }
            } catch (error) {
                console.error("Erro ao abrir ou baixar PDF:", error);
                alert("Não foi possível baixar o arquivo. Por favor, tente novamente mais tarde.");
            }
        };

        // Montar estrutura final
        container.appendChild(header);
        container.appendChild(viewerContainer);
        container.appendChild(downloadButton);

        return container;
    } catch (error) {
        console.error("Erro fatal ao criar visualizador:", error);
        showFallbackMessage();

        // Retornar um elemento vazio para evitar erros
        const emptyDiv = document.createElement('div');
        return emptyDiv;
    }
}

/**
 * Extrai base64 de um texto HTML e baixa como PDF
 * @param {string} htmlText - Texto HTML que contém base64
 * @param {string} fileName - Nome do arquivo a ser baixado (default: "documento.pdf")
 * @returns {boolean} - True se o download foi iniciado com sucesso
 */
export function downloadPdfFromBase64Html(htmlText, fileName = "documento.pdf") {
    try {
        // Extrair o base64 do HTML
        const base64Regex = /data:application\/pdf;base64,([^'"]*)/;
        const match = htmlText.match(base64Regex);

        if (!match || !match[1]) {
            console.error('Base64 não encontrado no HTML');
            return false;
        }

        const base64Data = match[1].trim();
        console.log("Base64 extraído, tamanho:", base64Data.length);

        // Gerar nome de arquivo único para evitar colisões
        const uniqueFileName = `pdf/${Date.now()}-${Math.floor(Math.random() * 1000)}-${fileName}`;

        // Abrir PDF via blob URL (sem necessidade de S3)
        openPdfInExternalBrowser(base64Data, uniqueFileName);

        return true;

    } catch (error) {
        console.error('Erro:', error);
        return false;
    }
}

/**
 * Trata PDF para dispositivos móveis
 * @param {string} base64Data - Dados do PDF em base64
 * @param {string} fileName - Nome do arquivo
 */
function handleMobilePdf(base64Data, fileName) {
    // Mostrar um modal de carregamento
    const loadingModal = showLoadingModal('Preparando seu documento...');

    try {
        // Verificar se temos acesso à API
        const apiUrl = getApiBaseUrl();

        if (!apiUrl) {
            // Se não temos API disponível, usar abordagem de visualização direta
            if (loadingModal && document.body.contains(loadingModal)) {
                document.body.removeChild(loadingModal);
            }

            // Mostrar link direto do base64
            const pdfDataUrl = `data:application/pdf;base64,${base64Data}`;
            showLinkModal(pdfDataUrl);
            return;
        }

        // Se temos acesso à API, enviar PDF para o servidor fazer upload
        fetch(`${apiUrl}/v1/upload-pdf`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${process.env.REACT_APP_API_TOKEN || ''}`
            },
            body: JSON.stringify({
                base64Data: base64Data,
                fileName: fileName
            })
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error(`Erro na requisição: ${response.status}`);
                }
                return response.json();
            })
            .then(data => {
                // Fechar modal de carregamento
                if (loadingModal && document.body.contains(loadingModal)) {
                    document.body.removeChild(loadingModal);
                }

                // Mostrar link retornado pelo servidor
                if (data && data.url) {
                    showLinkModal(data.url);
                } else {
                    throw new Error('URL não retornada pelo servidor');
                }
            })
            .catch(err => {
                console.error('Erro ao processar PDF:', err);

                // Fechar modal de carregamento
                if (loadingModal && document.body.contains(loadingModal)) {
                    document.body.removeChild(loadingModal);
                }

                // Fallback para mostrar o PDF diretamente se o servidor falhar
                const pdfDataUrl = `data:application/pdf;base64,${base64Data}`;
                showLinkModal(pdfDataUrl);
            });
    } catch (error) {
        console.error('Erro ao processar PDF para mobile:', error);

        // Fechar modal de carregamento
        if (loadingModal && document.body.contains(loadingModal)) {
            document.body.removeChild(loadingModal);
        }

        // Fallback para mostrar o PDF diretamente
        const pdfDataUrl = `data:application/pdf;base64,${base64Data}`;
        showLinkModal(pdfDataUrl);
    }
}

/**
 * Mostra um modal simples com uma mensagem de carregamento
 * @param {string} message - Mensagem a ser exibida
 * @returns {HTMLElement} - Elemento do modal
 */
function showLoadingModal(message) {
    const modal = document.createElement('div');
    modal.style.position = 'fixed';
    modal.style.top = '0';
    modal.style.left = '0';
    modal.style.width = '100%';
    modal.style.height = '100%';
    modal.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
    modal.style.display = 'flex';
    modal.style.justifyContent = 'center';
    modal.style.alignItems = 'center';
    modal.style.zIndex = '9999';

    const content = document.createElement('div');
    content.style.backgroundColor = 'white';
    content.style.padding = '20px';
    content.style.borderRadius = '5px';
    content.style.boxShadow = '0 2px 10px rgba(0, 0, 0, 0.2)';
    content.style.textAlign = 'center';

    content.innerHTML = `
        <div style="margin-bottom: 15px;">${message}</div>
        <div class="loading-spinner" style="width: 40px; height: 40px; border: 4px solid #f3f3f3; border-top: 4px solid #3498db; border-radius: 50%; margin: 0 auto; animation: spin 2s linear infinite;"></div>
    `;

    modal.appendChild(content);
    document.body.appendChild(modal);

    // Adicionar keyframes para a animação do spinner
    const style = document.createElement('style');
    style.innerHTML = `
        @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
        }
    `;
    document.head.appendChild(style);

    return modal;
}

/**
 * Mostra um modal com o link para o documento
 * @param {string} url - URL do documento
 */
function showLinkModal(url) {
    const modal = document.createElement('div');
    modal.style.position = 'fixed';
    modal.style.top = '0';
    modal.style.left = '0';
    modal.style.width = '100%';
    modal.style.height = '100%';
    modal.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
    modal.style.display = 'flex';
    modal.style.justifyContent = 'center';
    modal.style.alignItems = 'center';
    modal.style.zIndex = '9999';

    const content = document.createElement('div');
    content.style.backgroundColor = 'white';
    content.style.padding = '20px';
    content.style.borderRadius = '5px';
    content.style.boxShadow = '0 2px 10px rgba(0, 0, 0, 0.2)';
    content.style.textAlign = 'center';
    content.style.maxWidth = '80%';

    content.innerHTML = `
        <h3 style="margin-top: 0; color: #333;">Seu documento está pronto!</h3>
        <p>Clique no botão abaixo para visualizar o PDF. Este link estará disponível por 10 minutos.</p>
        <div style="margin: 20px 0;">
            <a href="${url}" target="_blank" id="pdf-link" style="display: inline-block; background-color: #4CAF50; color: white; padding: 10px 15px; text-decoration: none; border-radius: 4px; font-weight: bold;">Abrir Documento</a>
        </div>
        <p style="font-size: 12px; color: #666;">Observação: O link expira em 10 minutos.</p>
        <button id="close-modal" style="margin-top: 15px; padding: 8px 12px; background-color: #f1f1f1; border: none; border-radius: 4px; cursor: pointer;">Fechar</button>
    `;

    modal.appendChild(content);
    document.body.appendChild(modal);

    // Adicionar evento de clique ao botão de fechar
    document.getElementById('close-modal').addEventListener('click', () => {
        document.body.removeChild(modal);
    });

    // Evento de clique no link para registrar analytics (opcional)
    document.getElementById('pdf-link').addEventListener('click', () => {
        console.log('Link do PDF clicado');
    });
}

/**
 * Mostra um modal de erro
 * @param {string} message - Mensagem de erro
 */
function showErrorModal(message) {
    const modal = document.createElement('div');
    modal.style.position = 'fixed';
    modal.style.top = '0';
    modal.style.left = '0';
    modal.style.width = '100%';
    modal.style.height = '100%';
    modal.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
    modal.style.display = 'flex';
    modal.style.justifyContent = 'center';
    modal.style.alignItems = 'center';
    modal.style.zIndex = '9999';

    const content = document.createElement('div');
    content.style.backgroundColor = 'white';
    content.style.padding = '20px';
    content.style.borderRadius = '5px';
    content.style.boxShadow = '0 2px 10px rgba(0, 0, 0, 0.2)';
    content.style.textAlign = 'center';

    content.innerHTML = `
        <h3 style="margin-top: 0; color: #d9534f;">Erro</h3>
        <p>${message}</p>
        <button id="close-error" style="margin-top: 15px; padding: 8px 12px; background-color: #d9534f; color: white; border: none; border-radius: 4px; cursor: pointer;">Fechar</button>
    `;

    modal.appendChild(content);
    document.body.appendChild(modal);

    // Adicionar evento de clique ao botão de fechar
    document.getElementById('close-error').addEventListener('click', () => {
        document.body.removeChild(modal);
    });
}

/**
 * Mostra uma mensagem simples com instruções sobre o PDF
 */
function showPdfMessage() {
    try {
        // Criar um elemento de mensagem simples
        const messageElement = document.createElement('div');
        messageElement.style.position = 'fixed';
        messageElement.style.top = '50%';
        messageElement.style.left = '50%';
        messageElement.style.transform = 'translate(-50%, -50%)';
        messageElement.style.backgroundColor = 'white';
        messageElement.style.padding = '20px';
        messageElement.style.borderRadius = '8px';
        messageElement.style.boxShadow = '0 2px 10px rgba(0,0,0,0.2)';
        messageElement.style.maxWidth = '80%';
        messageElement.style.textAlign = 'center';
        messageElement.style.zIndex = '999999';

        // Conteúdo da mensagem
        messageElement.innerHTML = `
            <h3 style="margin-top: 0; color: #343a40;">Documento Disponível</h3>
            <p>O documento PDF está disponível para visualização.</p>
            <p>Por favor, entre em contato conosco pelos canais de atendimento para solicitar o envio por e-mail.</p>
            <p style="margin-bottom: 15px;">Ou acesse o documento diretamente pela área do cliente em nosso site.</p>
            <button id="close-pdf-message" style="background: #007bff; color: white; border: none; padding: 8px 15px; border-radius: 4px; cursor: pointer;">Entendi</button>
        `;

        // Adicionar ao corpo do documento
        document.body.appendChild(messageElement);

        // Configurar botão de fechar
        document.getElementById('close-pdf-message').addEventListener('click', function () {
            try {
                document.body.removeChild(messageElement);
            } catch (e) {
                console.error('Erro ao remover mensagem:', e);
            }
        });

    } catch (error) {
        console.error('Erro ao mostrar mensagem:', error);
        // Tentar um alerta simples como último recurso
        try {
            alert('O documento PDF está disponível. Por favor, entre em contato conosco para solicitar o envio por e-mail ou acesse pela área do cliente.');
        } catch (alertError) {
            console.error('Erro ao mostrar alerta:', alertError);
        }
    }
}

/**
 * Abre o PDF no navegador externo usando blob URL
 * @param {string} base64Data - Dados PDF em base64
 * @param {string} fileName - Nome do arquivo
 */
function openPdfInExternalBrowser(base64Data, fileName) {
    try {
        // 1. Criar blob do PDF
        const byteCharacters = atob(base64Data);
        const byteArrays = [];

        for (let offset = 0; offset < byteCharacters.length; offset += 512) {
            const slice = byteCharacters.slice(offset, offset + 512);

            const byteNumbers = new Array(slice.length);
            for (let i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
            }

            const byteArray = new Uint8Array(byteNumbers);
            byteArrays.push(byteArray);
        }

        const blob = new Blob(byteArrays, { type: 'application/pdf' });

        // 2. Criar URL a partir do blob
        const blobUrl = URL.createObjectURL(blob);

        // 3. Mostrar diálogo de confirmação com instruções
        const confirmDialog = document.createElement('div');
        confirmDialog.style.position = 'fixed';
        confirmDialog.style.top = '0';
        confirmDialog.style.left = '0';
        confirmDialog.style.width = '100%';
        confirmDialog.style.height = '100%';
        confirmDialog.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
        confirmDialog.style.display = 'flex';
        confirmDialog.style.justifyContent = 'center';
        confirmDialog.style.alignItems = 'center';
        confirmDialog.style.zIndex = '999999';

        const dialogContent = document.createElement('div');
        dialogContent.style.backgroundColor = 'white';
        dialogContent.style.padding = '20px';
        dialogContent.style.borderRadius = '8px';
        dialogContent.style.maxWidth = '80%';
        dialogContent.style.textAlign = 'center';

        dialogContent.innerHTML = `
            <h3 style="margin-top: 0; color: #333;">Visualizar PDF</h3>
            <p>O PDF será aberto no seu navegador. Clique no botão abaixo para continuar.</p>
            <div style="display: flex; justify-content: space-between; margin-top: 20px;">
                <button id="cancel-btn" style="padding: 10px 15px; background: #ccc; border: none; border-radius: 4px; cursor: pointer;">Cancelar</button>
                <button id="open-btn" style="padding: 10px 15px; background: #007bff; color: white; border: none; border-radius: 4px; cursor: pointer;">Abrir PDF</button>
            </div>
        `;

        confirmDialog.appendChild(dialogContent);
        document.body.appendChild(confirmDialog);

        // 4. Configurar botões de ação
        document.getElementById('cancel-btn').addEventListener('click', () => {
            document.body.removeChild(confirmDialog);
            URL.revokeObjectURL(blobUrl); // Limpar URL
        });

        document.getElementById('open-btn').addEventListener('click', () => {
            // Abrir no navegador externo
            window.open(blobUrl, '_blank');

            // Fechar diálogo após um curto atraso
            setTimeout(() => {
                try {
                    document.body.removeChild(confirmDialog);
                } catch (e) {
                    // Ignorar erros se o diálogo já foi removido
                }
            }, 500);

            // Programar limpeza do URL após um tempo
            setTimeout(() => {
                URL.revokeObjectURL(blobUrl);
            }, 60000); // Manter URL por 1 minuto
        });
    } catch (error) {
        console.error('Erro ao abrir PDF no navegador:', error);
        alert('Não foi possível abrir o PDF. Por favor, tente novamente mais tarde.');
    }
}

/**
 * Verifica se um botão (ou qualquer elemento) contém um PDF e inicia o download quando clicado
 * @param {Event} event - Evento de clique
 * @param {string} fileName - Nome opcional para o arquivo
 * @returns {boolean} - True se o download foi iniciado
 */
export function handlePdfButtonClick(event, fileName = "documento.pdf") {
    try {
        // Pegar o elemento HTML que foi clicado
        const element = event.currentTarget;

        if (!element) {
            console.error('Elemento não encontrado no evento de clique');
            return false;
        }

        // Extrair o conteúdo HTML do elemento
        const content = element.outerHTML || element.innerHTML;

        // Se não tiver conteúdo, verificar se o elemento tem um atributo data-pdf-content
        const pdfContent = content || element.getAttribute('data-pdf-content');

        if (!pdfContent) {
            console.error('Conteúdo PDF não encontrado no botão');
            return false;
        }

        // Verificar se contém dados de PDF em base64 e baixar
        if (pdfContent.includes('data:application/pdf;base64,')) {
            return downloadPdfFromBase64Html(pdfContent, fileName);
        } else {
            console.error('Botão não contém dados PDF em base64');
            return false;
        }
    } catch (error) {
        console.error('Erro ao processar clique no botão de PDF:', error);
        return false;
    }
}

/**
 * Processa um elemento de opção (botão) para extrair e baixar PDF
 * @param {Object} option - Objeto de opção com propriedade text contendo o HTML
 * @param {string} fileName - Nome do arquivo a ser baixado
 * @returns {boolean} - True se o download foi iniciado com sucesso
 */
export function downloadPdfFromOption(option, fileName = "documento.pdf") {
    try {
        if (!option || !option.text) {
            console.error('Opção inválida para download de PDF');
            return false;
        }

        if (option.text.includes('data:application/pdf;base64,')) {
            return downloadPdfFromBase64Html(option.text, fileName);
        }

        console.error('Opção não contém dados PDF em base64');
        return false;
    } catch (error) {
        console.error('Erro ao baixar PDF da opção:', error);
        return false;
    }
}

/**
 * Verifica se uma resposta da API contém um PDF em base64 para download
 * @param {Object} data - Resposta da API
 * @param {string} fileName - Nome opcional do arquivo
 * @returns {boolean} - True se um PDF foi detectado e o download iniciado
 */
export function checkAndDownloadPdfFromResponse(data, fileName = "documento.pdf") {
    try {
        // Verificar se temos opções e se a primeira opção contém um link com PDF em base64
        if (data && data.options && data.options.length > 0 && data.options[0].text) {
            const optionText = data.options[0].text;

            if (optionText.includes('data:application/pdf;base64,')) {
                return downloadPdfFromBase64Html(optionText, fileName);
            }
        }
        return false;
    } catch (error) {
        console.error('Erro ao verificar PDF na resposta:', error);
        return false;
    }
}

/**
 * Permite o download manual do PDF a partir da mensagem
 * @param {Object} message - Objeto de mensagem que contém a opção com o PDF
 * @param {string} fileName - Nome do arquivo a ser baixado
 * @returns {boolean} - True se o download foi iniciado com sucesso
 */
export function downloadPdfFromMessage(message, fileName = "documento.pdf") {
    try {
        if (!message || !message.options || !message.options.length) {
            console.error('Mensagem não contém opções para download de PDF');
            return false;
        }

        for (const option of message.options) {
            if (option.text && option.text.includes('data:application/pdf;base64,')) {
                return downloadPdfFromBase64Html(option.text, fileName);
            }
        }

        console.error('Nenhuma opção contendo PDF encontrada na mensagem');
        return false;
    } catch (error) {
        console.error('Erro ao tentar baixar PDF da mensagem:', error);
        return false;
    }
}

export function sendCSATFeedback(data) {
    // Certifica-se de que os campos obrigatórios estejam presentes
    const payload = {
        brandId: "17",
        regional: "SP",
        channel: "chatApp",
        client_id: data.client_id,
        foundWhatLookingFor: data.response === 'sim' ? 'Sim' : 'Não',
        uuid: `csat_${Date.now()}_${Math.floor(Math.random() * 100000)}`,
        autoservice: data.autoservice || "",
        organizationId: process.env.REACT_APP_ORGANIZATION_ID
    };

    // Adiciona o comentário se fornecido
    if (data.feedback) {
        payload.comment = data.feedback;
    }

    // Adiciona o rating se fornecido (para qualquer tipo de resposta)
    if (data.rating) {
        payload.csat = data.rating;
    }

    console.log("Enviando CSAT para a API:", payload);

    fetch(`${getApiBaseUrl()}/csat`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getAuthHeader()
        },
        body: JSON.stringify(payload)
    })
        .then(async response => {
            if (!response.ok) {
                throw new Error(`Erro na requisição: ${response.status} ${response.statusText}`);
            }
            const text = await response.text();
            if (!text || text.trim() === '') {
                return {};
            }
            try {
                return JSON.parse(text);
            } catch (error) {
                console.error('Erro ao processar resposta JSON:', error);
                console.error('Texto da resposta:', text);
                return {};
            }
        })
        .then(data => {
            // Resposta da API processada com sucesso
            console.log(data);
            console.log('Feedback CSAT enviado com sucesso');
        })
        .catch(error => {
            console.error('Erro ao enviar feedback CSAT:', error);
        });
};

export function sendFile(fileBase64, client_id, requestId, setMessage, messages, ocrType = 'normal', idPessoaFisica = null, marca = 'fleury', origem = 'app', sendMessageCallback = null, getLocation = null, operational_system = 'web', version_app = '1.0', setRequestId = null, ocrProvider = 'fleury') {

    // Detectar o tipo de arquivo baseado no base64
    const fileInfo = detectFileTypeFromBase64(fileBase64);
    const fileName = `${ocrType === 'pedido-medico' ? 'pedido_medico' : (ocrType === 'ocr-carteirinha' ? 'ocr_carteirinha' : 'arquivo')}_${uuidv4()}.${fileInfo.extension}`;
    let uploadedAttachment = null;

    // Remover prefixo do base64 (data:image/jpeg;base64,) antes de fazer qualquer envio
    const cleanBase64 = fileBase64.replace(/^data:[^;]+;base64,/, '');

    // Opt in only for supported OCR prompts. The backend must validate the conversation's saved route.
    if (ocrProvider === 'firstlayer' && (ocrType === 'pedido-medico' || ocrType === 'ocr-carteirinha')) {
        return sendFileToSignedUrl(cleanBase64, fileName, fileInfo)
            .then(signedUrl => {
                if (typeof sendMessageCallback !== 'function') {
                    throw new Error('Conversation callback unavailable');
                }
                return Promise.resolve(sendMessageCallback(
                    setMessage, client_id, '', messages, marca, origem,
                    null, null, null, null, null, null, getLocation,
                    operational_system, version_app, null,
                    { attachment: { url: signedUrl, fileName, mimeType: fileInfo.contentType } },
                    null, setRequestId
                )).then(result => {
                    if (result === false) throw new Error('Attachment dispatch failed');
                    return result;
                });
            })
            .catch(() => {
                setMessage(previous => [
                    ...previous.filter(message => !message.typing),
                    { origin: 'bot', response: 'Não foi possível enviar o documento. Por favor, tente enviá-lo novamente.',
                        options: [], bloqueiaInput: false, desativaFeedbackPorMensagem: true,
                        retryOcrUpload: { requestId, ocrType, idPessoaFisica, ocrProvider: 'firstlayer' } }
                ]);
                // Never retry the external route through Fleury OCR.
                return false;
            });
    }

    // Enviar arquivo via pre-signed URL (via REACT_APP_INSURANCE_API_URL)
    sendFileToSignedUrl(cleanBase64, fileName, fileInfo)
        .then(signedUrl => {
            uploadedAttachment = {
                url: signedUrl,
                fileName,
                mimeType: fileInfo.contentType,
            };
            // Agora enviar para a API do OCR com a URL assinada
            let payload;

            if (ocrType === 'pedido-medico') {

                // Payload V4 para pedido médico
                payload = {
                    clientId: client_id,
                    ignoreOCRSending: false,
                    type: 'pedido-medico',
                    useFileUploadV4: true,
                    idReferencia: requestId,
                    requestId: requestId,
                    idPessoaFisica: idPessoaFisica ? String(idPessoaFisica) : "",
                    idMarca: "17",
                    fileName: fileName,
                    file: signedUrl
                };
            } else if (ocrType === 'ocr-carteirinha') {
                // Payload específico para OCR da carteirinha (se necessário, pode incluir campos adicionais)
                payload = {
                    clientId: client_id,
                    ignoreOCRSending: false,
                    type: 'ocr-carteirinha',
                    idReferencia: requestId,
                    requestId: requestId,
                    idPessoaFisica: idPessoaFisica ? String(idPessoaFisica) : "",
                    idMarca: "17",
                    fileName: fileName,
                    file: signedUrl
                };
            } else {
                // Payload normal para OCR padrão
                payload = {
                    clientId: client_id,
                    ignoreOCRSending: false,
                    type: 'ocr-prescricao',
                    idReferencia: requestId,
                    requestId: requestId,
                    idPessoaFisica: idPessoaFisica ? String(idPessoaFisica) : "",
                    idMarca: "17",
                    fileName: fileName,
                    file: signedUrl
                };
            }

            // app-log removido

            console.log('📤 Enviando OCR para /send-ocr', {
                ocrType,
                requestId,
                fileName,
                uploadMethod: 'signedUrl'
            });
            return fetch(`${getApiBaseUrl()}/send-ocr`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...getAuthHeader()
                },
                body: JSON.stringify(payload)
            });
        })
        .then(response => response.json())
        .then(data => {
            console.log('✅ Resposta da API /send-ocr', { ocrType, requestId, data });
            // app-log removido

            // Se for pedido-médico, fazer nova chamada usando sendMessage
            if (ocrType === 'pedido-medico') {
                if (sendMessageCallback) {
                    // Fazer chamada automática usando sendMessage com input vazio
                    sendMessageCallback(
                        setMessage,
                        client_id,
                        "",  // message vazia
                        messages,
                        marca,
                        origem,
                        null, // teste
                        null, // localizacao
                        null, // latitude
                        null, // longitude
                        null, // lastInput
                        null, // setTeste
                        getLocation,
                        operational_system,
                        version_app,
                        null, // examesSelecionados
                        uploadedAttachment ? { attachment: uploadedAttachment } : null, // additionalContext
                        null, // requestImage
                        setRequestId
                    );
                } else {
                    console.log('⚠️ OCR Pedido Médico processado, mas sendMessageCallback não está disponível');
                }
            } else if (ocrType === 'ocr-carteirinha') {
                // Não chamar sendMessage aqui; o fluxo continua via polling do SQS no chat
            }
        })
        .catch(error => {
            console.error('❌ Erro ao enviar arquivo para /send-ocr:', error);
            // app-log removido

            // Fallback: enviar base64 direto para a API OCR
            let fallbackPayload;

            if (ocrType === 'pedido-medico') {

                // Payload V4 para pedido médico (fallback)
                fallbackPayload = {
                    clientId: client_id,
                    ignoreOCRSending: false,
                    type: 'pedido-medico',
                    useFileUploadV4: true,
                    idReferencia: requestId,
                    requestId: requestId,
                    idPessoaFisica: idPessoaFisica ? String(idPessoaFisica) : "",
                    idMarca: "17",
                    fileName: fileName,
                    file: cleanBase64,
                    uploadMethod: 'base64_fallback'
                };
            } else if (ocrType === 'ocr-carteirinha') {
                // Payload específico para OCR da carteirinha (fallback)
                fallbackPayload = {
                    clientId: client_id,
                    ignoreOCRSending: false,
                    type: 'ocr-carteirinha',
                    idReferencia: requestId,
                    requestId: requestId,
                    idPessoaFisica: idPessoaFisica ? String(idPessoaFisica) : "",
                    idMarca: "17",
                    fileName: fileName,
                    file: cleanBase64,
                    uploadMethod: 'base64_fallback'
                };
            } else {

                // Payload normal para OCR padrão (fallback)
                fallbackPayload = {
                    clientId: client_id,
                    ignoreOCRSending: false,
                    type: 'ocr-prescricao',
                    idReferencia: requestId,
                    requestId: requestId,
                    idPessoaFisica: idPessoaFisica ? String(idPessoaFisica) : "",
                    idMarca: "17",
                    fileName: fileName,
                    file: cleanBase64,
                    uploadMethod: 'base64_fallback'
                };
            }

            console.log('📤 Enviando OCR (FALLBACK) para /send-ocr', {
                ocrType,
                requestId,
                fileName,
                uploadMethod: 'base64_fallback'
            });
            fetch(`${getApiBaseUrl()}/send-ocr`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...getAuthHeader()
                },
                body: JSON.stringify(fallbackPayload)
            })
                .then(response => response.json())
                .then(data => {
                    console.log('✅ Resposta da API OCR (fallback):', { ocrType, requestId, data });
                    // app-log removido

                    // Se for pedido-médico, fazer nova chamada usando sendMessage
                    if (ocrType === 'pedido-medico') {
                        if (sendMessageCallback) {
                            // Fazer chamada automática usando sendMessage com input vazio
                            sendMessageCallback(
                                setMessage,
                                client_id,
                                "",  // message vazia
                                messages,
                                marca,
                                origem,
                                null, // teste
                                null, // localizacao
                                null, // latitude
                                null, // longitude
                                null, // lastInput
                                null, // setTeste
                                getLocation,
                                operational_system,
                                version_app,
                                null, // examesSelecionados
                                uploadedAttachment ? { attachment: uploadedAttachment } : null, // additionalContext
                                null, // requestImage
                                setRequestId
                            );
                        } else {
                            console.log('⚠️ OCR Pedido Médico (FALLBACK) processado, mas sendMessageCallback não está disponível');
                        }
                    } else if (ocrType === 'ocr-carteirinha') {
                        // Não chamar sendMessage no fallback; polling do SQS seguirá no chat
                    }
                })
                .catch(fallbackError => {
                    console.error('Erro no fallback também:', fallbackError);
                });
        });
}

/**
 * Detecta o tipo de arquivo baseado no conteúdo base64
 * @param {string} base64Data - Dados em base64
 * @returns {Object} - Informações do tipo de arquivo
 */
function detectFileTypeFromBase64(base64Data) {
    // Primeiro verificar se há um prefixo data URL
    const dataUrlMatch = base64Data.match(/^data:([^;]+);base64,/);
    if (dataUrlMatch) {
        const mimeType = dataUrlMatch[1];

        switch (mimeType) {
            case 'application/pdf':
                return {
                    extension: 'pdf',
                    contentType: 'application/pdf',
                    type: 'pdf'
                };
            case 'image/jpeg':
            case 'image/jpg':
                return {
                    extension: 'jpg',
                    contentType: 'image/jpeg',
                    type: 'image'
                };
            case 'image/png':
                return {
                    extension: 'png',
                    contentType: 'image/png',
                    type: 'image'
                };
        }
    }

    // Se não encontrou o prefixo data URL, analisar os primeiros bytes
    const cleanBase64 = base64Data.replace(/^data:[^;]+;base64,/, '');
    const firstBytes = cleanBase64.substring(0, 10);

    // Assinaturas de diferentes tipos de arquivo em base64
    if (firstBytes.startsWith('JVBERi')) {
        // PDF (starts with %PDF in base64)
        return {
            extension: 'pdf',
            contentType: 'application/pdf',
            type: 'pdf'
        };
    } else if (firstBytes.startsWith('/9j/')) {
        // JPEG
        return {
            extension: 'jpg',
            contentType: 'image/jpeg',
            type: 'image'
        };
    } else if (firstBytes.startsWith('iVBORw0KGgo')) {
        // PNG
        return {
            extension: 'png',
            contentType: 'image/png',
            type: 'image'
        };
    } else {
        // Padrão para imagem JPEG se não conseguir identificar
        console.warn('Tipo de arquivo não identificado, assumindo JPEG. Primeiros bytes:', firstBytes);
        return {
            extension: 'jpg',
            contentType: 'image/jpeg',
            type: 'image'
        };
    }
}

/**
 * Upload de arquivo via pre-signed URL (sem aws-sdk direto)
 * @param {string} base64Data - Dados do arquivo em base64
 * @param {string} fileName - Nome do arquivo
 * @param {Object} fileInfo - Informações do tipo de arquivo
 * @returns {Promise<string>} - Promise que resolve com a URL do arquivo
 */
async function sendFileToSignedUrl(base64Data, fileName, fileInfo) {
    try {
        console.log(`🚀 Iniciando upload via pre-signed URL`);

        const folder = process.env.REACT_APP_OCR_FOLDER || "ocr-files";
        const url = process.env.REACT_APP_INSURANCE_API_URL;
        const apiKey = process.env.REACT_APP_INSURANCE_API_KEY;

        const body = {
            "context": {
                "action": "CreateS3UploadSignedLinkAction",
                "s3Upload": {
                    "folder": folder,
                    "fileName": fileName,
                    "contentType": fileInfo.contentType
                }
            }
        };

        let s3Data;

        // Fluxo Seguro via BFF (sem expor x-api-key)
        if (process.env.REACT_APP_BFF_API_URL || !process.env.REACT_APP_INSURANCE_API_KEY) {
            const data = await bffExecuteInsuranceAction(body.context, '');
            if (data.status !== 'success' && !data.context?.s3UploadSignedLink) {
                throw new Error('Falha ao obter URL pre-assinada via BFF: ' + JSON.stringify(data));
            }
            s3Data = data.context.s3UploadSignedLink;
        } else {
            // 1. Obter a URL pre-assinada (Legacy)
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-api-key': apiKey
                },
                body: JSON.stringify(body)
            });

            if (!response.ok) {
                throw new Error(`Erro ao solicitar presigned URL: ${response.status} ${response.statusText}`);
            }

            const data = await response.json();

            if (data.status !== 'success') {
                throw new Error('Falha ao obter URL pre-assinada: ' + JSON.stringify(data));
            }

            s3Data = data.context.s3UploadSignedLink;
        }

        const uploadUrl = s3Data.uploadUrl;
        const downloadUrl = s3Data.downloadUrl;

        // 2. Preparar o arquivo
        const cleanBase64 = base64Data.replace(/^data:[^;]+;base64,/, '');
        const binaryString = atob(cleanBase64);
        const fileBuffer = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            fileBuffer[i] = binaryString.charCodeAt(i);
        }

        console.log(`🚀 Fazendo upload para a URL pre-assinada...`);
        // 3. Fazer o upload do arquivo
        const uploadResponse = await fetch(uploadUrl, {
            method: 'PUT',
            headers: {
                'Content-Type': s3Data.contentType || fileInfo.contentType
            },
            body: fileBuffer
        });

        if (!uploadResponse.ok) {
            throw new Error(`Erro ao fazer upload: ${uploadResponse.status} ${uploadResponse.statusText}`);
        }

        return downloadUrl;

    } catch (error) {
        console.error('❌ Erro no upload:', error);
        throw error;
    }
}
